import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import ReactPaginate from 'react-paginate';
import {
    FiCreditCard, FiRefreshCw, FiDownload, FiPrinter,
    FiSearch, FiCheckCircle, FiAlertCircle, FiEye, FiCornerUpLeft, FiCornerUpRight, FiFolder, FiX, FiCheck, FiArrowRight, FiAlertTriangle, FiEdit,
    FiArrowDownLeft, FiArrowUpRight, FiInfo, FiAlertOctagon
} from 'react-icons/fi';
import {
    Card, Row, Col, Form, Table, Button, Badge,
    Spinner, Alert, Modal, InputGroup, Tabs, Tab, Offcanvas
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';

const BankReconciliationReport = () => {
    // State management
    const [bankTransactions, setBankTransactions] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        date: '',
        amount: '',
        reference: '',
        status: 'all',
        dc: 'all'
    });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const [reconciliationDate, setReconciliationDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showMatchModal, setShowMatchModal] = useState(false);
    const [selectedBankTxn, setSelectedBankTxn] = useState(null);
    const [pendingExpenses, setPendingExpenses] = useState([]);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [showFullView, setShowFullView] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [clients, setClients] = useState([]);
    const [reconciliationDetails, setReconciliationDetails] = useState(null);
    const [ledgers, setLedgers] = useState([]);
    const [ledgerSummary, setLedgerSummary] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    // API configuration
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const token = userInfo?.token;
    const origin = localStorage.getItem("user-origin");
    const Api = process.env.REACT_APP_WEBZASH_URL;
    const apiKey = process.env.REACT_APP_API_KEY;

    // Date format conversion helpers
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (error) {
            return dateString;
        }
    };

    const formatDateForBackend = (ddmmyyyy) => {
        if (!ddmmyyyy) return '';
        try {
            const parts = ddmmyyyy.split('-');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`; // yyyy-MM-dd
            }
            return ddmmyyyy;
        } catch (error) {
            return ddmmyyyy;
        }
    };

    const formatDate = formatDateForDisplay;

    const renderProjectBadges = (txn) => {
        const projects = txn?.projects || txn?._rawTransaction?.projects || [];
        const project = txn?.project || txn?._rawTransaction?.project || txn?._rawTransaction?.project_details;

        const projectList = Array.isArray(projects) && projects.length > 0
            ? projects
            : project
                ? [project]
                : [];

        if (projectList.length === 0) return <span className="text-muted small">N/A</span>;

        return (
            <div className="d-flex flex-wrap gap-2">
                {projectList.map((p, idx) => (
                    <div key={idx} className="badge bg-soft-warning text-warning p-2 px-3 rounded-pill fw-medium">
                        {p.name || (p.Project && p.Project.name) || (typeof p === 'string' ? p : 'Unknown Project')}
                    </div>
                ))}
            </div>
        );
    };

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch ERP Data
                const [
                    accountsRes,
                    expensesRes,
                    invoicesRes,
                    vendorsRes,
                    clientsRes
                ] = await Promise.all([
                    axios.get(`${backendUrl}/api/bank-accounts`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin,
                        }
                    }),
                    axios.get(`${backendUrl}/api/expense?reconciliation_status=pending`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin,
                        }
                    }),
                    axios.get(`${backendUrl}/api/proformainvoice?reconciliation_status=pending`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin,
                        }
                    }),
                    axios.get(`${backendUrl}/api/contacts?type=vendor`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin,
                        }
                    }),
                    axios.get(`${backendUrl}/api/contacts?type=client`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin,
                        }
                    })
                ]);

                setBankAccounts(accountsRes.data?.account || []);
                setPendingExpenses(expensesRes.data?.data || []);
                setPendingInvoices(invoicesRes.data?.proformainvoice || []);
                setVendors(vendorsRes.data?.data || []);
                setClients(clientsRes.data?.data || []);

                // Fetch WebZash Data independently
                try {
                    const ledgersRes = await axios.get(`${Api}/api/ledgers/index`, {
                        headers: {
                            'x-api-key': apiKey,
                            'Accept': 'application/json',
                        }
                    });
                    setLedgers(ledgersRes.data?.data || []);
                } catch (webzashError) {
                    console.error("WebZash ledgers fetch error:", webzashError);
                    setLedgers([]);
                }

            } catch (error) {
                console.error("ERP Initialization error:", error);
            }
            setLoading(false);
        };

        fetchInitialData();
    }, []);

    // Removed auto-fetch on bank selection - data loads only when Apply button is clicked
    // useEffect(() => {
    //     if (selectedBank) {
    //         fetchBankTransactions();
    //     }
    // }, [selectedBank]);

    const fetchBankTransactions = async (filterStartDate = null, filterEndDate = null, amount = null) => {
        setLoading(true);
        try {
            // Get proper backend dates
            const finalStartDate = filterStartDate || startDate;
            const finalEndDate = filterEndDate || endDate;

            // Step 1: Get selected bank account object
            const selectedAccount = bankAccounts.find(acc => acc.id === Number(selectedBank));

            // Trigger summary refresh IMMEDIATELY if we have the account and ledgers
            // This ensures summary cards show even if there are no transactions
            if (selectedAccount && ledgers.length > 0) {
                const matchedLedger = ledgers.find(l =>
                    (l.Ledger?.name === selectedAccount.name) ||
                    (l.name === selectedAccount.name)
                );

                if (matchedLedger) {
                    const ledgerId = matchedLedger.Ledger?.id || matchedLedger.id;
                    fetchLedgerSummary(
                        ledgerId,
                        finalStartDate,
                        finalEndDate
                    );
                }
            }

            // Step 2: Get ERP transactions
            const params = { account_id: selectedBank };
            if (finalStartDate) params.start_date = finalStartDate;
            if (finalEndDate) params.end_date = finalEndDate;

            if (transactionTypeFilter === 'petty_cash') {
                params.payment_mode = 'petty_cash';
            } else if (transactionTypeFilter) {
                params.type = transactionTypeFilter;
            }

            const transactionsRes = await axios.get(`${backendUrl}/api/transactions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
                params
            });

            const transactionsData = transactionsRes.data?.data || [];

            if (transactionsData.length === 0) {
                setBankTransactions([]);
                setLoading(false);
                return;
            }

            if (!selectedAccount) {
                setBankTransactions([]);
                setLoading(false);
                return;
            }

            // Step 3: Get ledger entries for matching
            const entriesRes = await axios.get(`${Api}/api/entries/getEntriesByLedger`, {
                headers: { 'x-api-key': apiKey },
                params: {
                    ledger_name: selectedAccount.name
                }
            });

            const entries = entriesRes.data?.entries || [];

            // Step 4: Match transactions with entries
            const combinedData = [];
            transactionsData.forEach(txn => {
                // Match original transaction with entries
                const matchedEntry = entries.find(e => {
                    const entry = e?.Entry;
                    return (
                        entry &&
                        (entry.id == txn.entry_id || (Number(entry.amount) === Number(txn.amount) && entry.date === txn.date))
                    );
                });

                combinedData.push({
                    ...txn,
                    matchedEntry: matchedEntry || null
                });

                // If transaction is reversed, inject the reversal row
                if (txn.reconciliation_status === 'reversed') {
                    // Find the reversal entry in Webzash if it exists (using reverse_entry_id)
                    const matchedReversal = entries.find(e => {
                        const entry = e?.Entry;
                        return entry && (entry.id == txn.reverse_entry_id);
                    });

                    // Create a virtual reversal transaction
                    // If matched reversal entry exists, use its amount as the 'Bank' amount (includes charges)
                    // If not, use the recorded reverse_amount or original amount as fallback
                    const reversalAmount = matchedReversal ? matchedReversal.Entry.entrytotal : (txn.reverse_amount || txn.amount);

                    combinedData.push({
                        ...txn,
                        id: `rev-${txn.id}`,
                        is_reversal_row: true,
                        // Override properties for reversal movement
                        amount: reversalAmount,
                        date: txn.reversal_date || txn.date,
                        matchedEntry: matchedReversal || null,
                        reconciliation_status: matchedReversal ? 'reconciled' : 'pending',
                        description: `Reversal: ${txn.reversal_reason || txn.description || ''}`,
                        reference_no: txn.reference_no ? `${txn.reference_no}-REV` : 'REVERSAL',
                        _rawTransaction: txn // Important: Store original txn for reference
                    });
                }
            });

            processBankTransactions(combinedData, bankAccounts);

        } catch (err) {
            console.error('Error loading bank transactions:', err);
            setBankTransactions([]);
            setLoading(false);
        }
    };

    const fetchLedgerSummary = async (ledgerId, startDate = null, endDate = null) => {
        try {
            const params = {
                ledger_id: ledgerId,
                start_date: startDate,
                end_date: endDate
            };

            const response = await axios.get(`${Api}/api/reports/ledgerstatement`, {
                headers: {
                    'x-api-key': apiKey,
                    'Accept': 'application/json',
                },
                params
            });

            if (response.data) {
                setLedgerSummary({
                    openingBalance: response.data.opening_balance,
                    periodTotals: response.data.period_totals,
                    closingBalance: response.data.closing_balance,
                    ledger: response.data.ledger
                });
            }
        } catch (error) {
            console.error('Error fetching ledger summary:', error);
            setLedgerSummary(null);
        }
    };


    const processBankTransactions = (transactions, banksList) => {
        const processed = transactions.map(txn => {
            const entry = txn.matchedEntry?.Entry || {};
            const entryItem = txn.matchedEntry?.Entryitem || {};

            // Explicitly determine type from entry data
            // const entrytype_id = entry.entrytype_id ||
            //     (txn.type === 'expense' ? 2 :
            //         txn.type === 'income' ? 1 : 3);
            const rawType = txn.type; // From backend API

            // Set transactionType based on raw type
            let transactionType = 'other';
            let entrytype_id = entry.entrytype_id;

            if (rawType === 'advance') {
                transactionType = 'advance';
                const advType = (txn.advance_type || txn.advance_details?.advance_type || '').toLowerCase();
                entrytype_id = entrytype_id || (advType === 'client' ? 1 : 2); // Receipt (1) for client, Payment (2) for vendor
            } else if (rawType === 'contra') {
                transactionType = 'contra';
                entrytype_id = entrytype_id || 4;

                // Debug logging for bank lookup
                console.log('🔍 Contra Processing:', {
                    id: txn.id,
                    from_id: txn.from_bank_id,
                    to_id: txn.to_bank_id,
                    availableBanks: banksList?.length,
                    bankSample: banksList?.[0]
                });

                const fromBank = banksList.find(b => b.id == txn.from_bank_id);
                const toBank = banksList.find(b => b.id == txn.to_bank_id);

                console.log('✅ Contra Banks Found:', {
                    from: fromBank?.name,
                    to: toBank?.name,
                    from_bank_id_type: typeof txn.from_bank_id,
                    bank_id_type: typeof banksList?.[0]?.id
                });

                // Handle bank to bank, bank to cash, or cash to bank transfers
                txn.from_bank_name = fromBank?.name || (txn.from_bank_id ? `Account: ${txn.from_bank_id}` : 'N/A');
                txn.to_bank_name = toBank?.name || (txn.to_bank_id ? `Account: ${txn.to_bank_id}` : 'N/A');

                // Track which side of the contra the selected bank is on
                txn.isSelectedTo = String(txn.to_bank_id) === String(selectedBank);
                txn.isSelectedFrom = String(txn.from_bank_id) === String(selectedBank);

                console.log('from', txn.from_bank_name);
            } else if (rawType === 'vat_payable') {
                transactionType = 'vat_payable';
                entrytype_id = entrytype_id || 4; // Use appropriate entry type
            } else if (rawType === 'expense') {
                transactionType = 'payment';
                entrytype_id = entrytype_id || 2;
            } else if (rawType === 'income') {
                transactionType = 'receipt';
                entrytype_id = entrytype_id || 1;
            } else if (rawType === 'capital') {
                transactionType = 'capital';
                entrytype_id = entrytype_id || 3;
            } else if (rawType === 'payroll') {
                transactionType = 'payroll';
                entrytype_id = entrytype_id || 2; // Usually payment type
            } else if (rawType === 'withdrawal') {
                transactionType = 'withdrawal';
                entrytype_id = entrytype_id || 2; // Default to payment for withdrawal
            } else if (rawType === 'bank_guarantee') {
                transactionType = 'bank_guarantee';
                entrytype_id = entrytype_id || 2;
            } else if (rawType === 'bank_guarantee_released') {
                transactionType = 'bank_guarantee_released';
                entrytype_id = entrytype_id || 1;
            } else if (rawType === 'bank_guarantee_charges') {
                transactionType = 'bank_guarantee_charges';
                entrytype_id = entrytype_id || 2;
            } else if (rawType === 'bad_debt_written_off') {
                transactionType = 'bad_debt_written_off';
                entrytype_id = entrytype_id || 2;
            } else {
                // Fallback to entrytype_id if raw type not available
                transactionType = entrytype_id === 1 ? 'receipt' :
                    entrytype_id === 2 ? 'payment' : 'capital';
            }


            // Handle Reversal Row Specific Logic
            if (txn.is_reversal_row) {
                // If it's a reversal row, try to determine type from the matched entry
                if (entry.entrytype_id) {
                    entrytype_id = entry.entrytype_id;
                    transactionType = entrytype_id === 1 ? 'receipt' :
                        entrytype_id === 2 ? 'payment' : 'other';
                } else {
                    // Fallback: flip the original type if no entry match
                    if (transactionType === 'payment') {
                        transactionType = 'receipt';
                        entrytype_id = 1;
                    } else if (transactionType === 'receipt') {
                        transactionType = 'payment';
                        entrytype_id = 2;
                    }
                }

                // For contra reversal, handle specifically
                if (rawType === 'contra') {
                    // Logic to flip from/to for contra reversal if needed
                }
            }

            // Get entry ID - IMPORTANT: Use txn.entry_id from raw transaction data
            const entryId = txn.is_reversal_row ? txn.reverse_entry_id : txn.entry_id;

            const transactionCurrency =
                txn?.accounts?.currency?.currency || 
                (typeof txn?.currency === 'object' ? txn?.currency?.currency : txn?.currency) || 
                'AED';

            let invoiceItemsTotal = 0;
            let invoiceItemsPaid = 0;
            let expenseItemsTotal = 0;
            let expenseItemsPaid = 0;

            // Process transaction_invoice_items if available
            if (txn.transaction_invoice_items && Array.isArray(txn.transaction_invoice_items)) {
                txn.transaction_invoice_items.forEach(item => {
                    invoiceItemsTotal += parseFloat(item.amount || 0);
                    invoiceItemsPaid += parseFloat(item.paid_amount || 0);
                });
            }

            // Process transaction_expense_items if available
            if (txn.transaction_expense_items && Array.isArray(txn.transaction_expense_items)) {
                txn.transaction_expense_items.forEach(item => {
                    expenseItemsTotal += parseFloat(item.amount || 0);
                    expenseItemsPaid += parseFloat(item.paid_amount || 0);
                });
            }

            const totalVoucherAmount = invoiceItemsTotal + expenseItemsTotal;
            const totalVoucherPaid = invoiceItemsPaid + expenseItemsPaid;

            const parseSafeFloat = (val) => {
                if (typeof val === 'number') return val;
                if (!val) return 0;
                return parseFloat(val.toString().replace(/,/g, ''));
            };

            const wpsCharge = parseSafeFloat(txn.wps_charge || txn.bank_service_charge || 0);
            const payrollVat = parseSafeFloat(txn.wps_vat || txn.bank_vat_amount || 0);
            const bankAmount = parseSafeFloat(txn.amount || 0);
            const netPayrollAmount = (bankAmount - wpsCharge - payrollVat) || 0;

            let finalAmount = Math.abs(parseSafeFloat(entryItem.amount || txn.amount || 0));
            if (rawType === 'bank_guarantee') {
                const commFee = parseSafeFloat(txn.commission_fee || txn.bg_details?.commission_fee || 0);
                const commVat = parseSafeFloat(txn.commission_vat || txn.bg_details?.commission_vat || 0);
                const bankCharge = parseSafeFloat(txn.bank_service_charge || 0);
                const bankVat = parseSafeFloat(txn.bank_vat_amount || 0);
                finalAmount += (commFee + commVat + bankCharge + bankVat);
            } else if (rawType === 'payroll' || txn.is_payroll) {
                const bankCharge = parseSafeFloat(txn.wps_charge || txn.bank_service_charge || 0);
                const bankVat = parseSafeFloat(txn.wps_vat || txn.bank_vat_amount || 0);
                finalAmount += (bankCharge + bankVat);
            }

            return {
                id: txn.id,
                entry_id: entryId,
                payment_mode: txn.payment_mode,
                journal_id: entry.journal_id || txn.journal_id,
                date: entry.date || txn.date,
                created_at: txn.created_at,
                amount: finalAmount,
                reference: entry.reference_no || txn.reference_no || '',
                description: entry.narration || txn.description || '',
                currency: transactionCurrency,
                entrytype_id: entrytype_id, // Use the explicitly determined type
                reconciliation_status: txn.reconciliation_status || 'pending',
                ledger_name: entryItem.ledger_name || '',
                dc: (entryItem.dc || '').toString().toUpperCase(), // Added DC flag
                entry_item: entryItem, // Keep entry item for reference
                type: transactionType,
                vendor_id: txn.vendor_id || entry.client_id,
                client_id: txn.client_id,
                expense_id: txn.expense_id,
                vendor: txn?.vendor,
                client: txn?.client,
                account: txn?.account?.currency,
                accounts: txn?.account,
                exchange_rate: txn?.exchange_rate || 1, // Ensure exchange rate is set
                invoice: txn?.invoice || null,
                expense: txn?.expense || null,
                deduction_amount: txn?.deduction_amount,
                bank_service_charge: txn?.bank_service_charge || 0,
                bank_vat_amount: txn?.bank_vat_amount || 0,
                invoices: txn?.invoices,
                transaction_voucher_id: txn.transaction_voucher_id || '', // Add if not exists
                reference_no: txn.reference_no || '', // Bank reference number
                reverse_amount: txn.reverse_amount || 0, // For reversals
                reverse_service_charge: txn.reverse_service_charge || 0,
                reverse_vat: txn.reverse_vat || 0,
                reverse_deduction: txn.reverse_deduction || 0,
                is_reversed: txn.is_reversed || false,
                reversal_date: txn.reversal_date || null,
                reversal_reason: txn.reversal_reason || '',
                entryID: entryId || txn.entry_id,
                wps_charge: wpsCharge,
                wps_vat: payrollVat,
                bank_amount: bankAmount,
                net_payroll_amount: netPayrollAmount,
                is_payroll: rawType === 'payroll' || txn.is_payroll,
                is_contra: transactionType === 'contra',
                is_vat_payable: transactionType === 'vat_payable',
                advance_type: txn.advance_type || txn.advance_details?.advance_type,
                from_bank_name: txn.from_bank_name,
                to_bank_name: txn.to_bank_name,
                isSelectedTo: txn.isSelectedTo,
                isSelectedFrom: txn.isSelectedFrom,
                is_withdrawal: rawType === 'withdrawal',
                is_bank_guarantee: rawType === 'bank_guarantee',
                is_bank_guarantee_released: rawType === 'bank_guarantee_released',
                is_bank_guarantee_charges: rawType === 'bank_guarantee_charges',
                is_bad_debt_written_off: rawType === 'bad_debt_written_off',
                commission_fee: txn.commission_fee || txn.bg_details?.commission_fee || 0,
                commission_vat: txn.commission_vat || txn.bg_details?.commission_vat || 0,
                guarantee_id: txn.guarantee_id || txn.bg_details?.guarantee_id,
                guarantee_name: txn.guarantee_name || txn.bg_details?.guarantee_name,


                // NEW: Transaction items for reconciliation
                transaction_invoice_items: txn.transaction_invoice_items || [],
                transaction_expense_items: txn.transaction_expense_items || [],

                // NEW: Voucher totals
                voucher_totals: {
                    invoice_items_total: invoiceItemsTotal,
                    invoice_items_paid: invoiceItemsPaid,
                    invoice_items_balance: invoiceItemsTotal - invoiceItemsPaid,
                    expense_items_total: expenseItemsTotal,
                    expense_items_paid: expenseItemsPaid,
                    expense_items_balance: expenseItemsTotal - expenseItemsPaid,
                    total_voucher_amount: totalVoucherAmount,
                    total_voucher_paid: totalVoucherPaid,
                    total_voucher_balance: totalVoucherAmount - totalVoucherPaid
                },

                // Projects
                projects: txn.projects || [],

                // Add raw data for debugging
                _rawTransaction: txn,
                _rawEntry: entry
            };
        });
        console.log('Processed Transactions with types:', processed.map(t => ({
            id: t.id,
            type: t.type,
            entrytype_id: t.entrytype_id,
            rawType: t._rawTransaction?.type
        })));
        console.log('Processed Transactions:', processed);
        setBankTransactions(processed);
        setLoading(false);
    };

    const getTransactionType = (typeId) => {
        switch (typeId) {
            case 1: return 'receipt';
            case 2: return 'payment';
            default: return 'other';
        }
    };
    const isSameQuarter = (dateStr) => {
        const txnDate = new Date(reconciliationDate);
        const itemDate = new Date(dateStr);

        const txnQuarter = Math.floor(txnDate.getMonth() / 3);
        const itemQuarter = Math.floor(itemDate.getMonth() / 3);

        return txnDate.getFullYear() === itemDate.getFullYear() && txnQuarter === itemQuarter;
    };


    const fetchReconciliationDetails = async (transactionId) => {
        try {
            const response = await axios.get(`${backendUrl}/api/reconciliation-details/${transactionId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                }
            });
            setReconciliationDetails(response.data);
        } catch (error) {
            console.error("Error fetching reconciliation details:", error);
        }
    };

    const reconcileTransaction = async (bankTxn, item) => {
        try {
            // Determine correct item type based on transaction type
            // const itemType = bankTxn.entrytype_id === 2 ? 'expense' : 'invoice';
            let itemType;

            if (bankTxn.is_payroll || item.type === 'payroll') {
                itemType = 'expense';
            } else if (bankTxn.entrytype_id === 3 || bankTxn.isAdvance) {
                itemType = 'expense';
            } else if (bankTxn.entrytype_id === 2) {
                itemType = 'expense';
            } else {
                itemType = 'invoice';
            }

            // FIX: Use the correct entry_id
            const entryId = bankTxn.entry_id || bankTxn.entryId || bankTxn.entryID;
            const payload = {
                transaction_id: bankTxn.id,
                entry_id: entryId,
                item_id: item.id,
                item_type: itemType,  // Use the determined type
                reconciliation_date: reconciliationDate,
                vendor_id: bankTxn.vendor_id,
                client_id: bankTxn.client_id
            };
            if (bankTxn.isAdvance) {
                payload.advance_details = {
                    ledger_entry_id: bankTxn.entryId,
                    voucher_type: 'payment_voucher', // or 'receipt_voucher' based on your system
                    is_advance_payment: true
                };
            }
            if (bankTxn.is_payroll || item.type === 'payroll') {
                payload.is_payroll = true;
                if (item.payroll_details) {
                    payload.payroll_details = item.payroll_details;
                }
            }

            console.log('Reconciliation Payload:', payload); // Debug log

            const response = await axios.post(`${backendUrl}/api/reconcile`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                }
            });

            if (response.data.success) {
                return true;
            }
            return false;
        } catch (error) {
            console.error("Reconciliation error:", error.response?.data || error.message);
            return false;
        }
    };
    const reconcileMultipleTransactions = async (bankTxn, items) => {
        try {
            const itemType = bankTxn.entrytype_id === 2 ? 'expense' : 'invoice';

            const payload = {
                transaction_id: bankTxn.id,
                entry_id: bankTxn.entry_id,
                item_ids: items.map(item => item.id), // Array of IDs
                item_type: itemType,
                reconciliation_date: reconciliationDate,
                vendor_id: bankTxn.vendor_id,
                client_id: bankTxn.client_id,
                is_multi_expense: true // Flag for backend
            };

            console.log('Multi-Expense Payload:', payload);

            const response = await axios.post(`${backendUrl}/api/reconcile-multi`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                }
            });

            if (response.data.success) {
                return true;
            }
            return false;
        } catch (error) {
            console.error("Multi-reconciliation error:", error.response?.data || error.message);
            return false;
        }
    };
    // const handleReconcileClick = async (txn) => {
    //      console.log('🔍 Transaction details for reconciliation:', {
    //     id: txn.id,
    //     type: txn.type,
    //     entrytype_id: txn.entrytype_id,
    //     entry_id: txn.entry_id,
    //     entryId: txn.entryId,
    //     entryID: txn.entryID,
    //     matchedEntry: txn._rawEntry, // Check if we have a matched entry
    //     rawTransaction: txn._rawTransaction // Check original transaction data
    // });
    //     // Create modal-ready transaction object
    //     // const modalTxn = {
    //     //     ...txn,
    //     //     // Ensure type is properly set
    //     //     entrytype_id: txn.entrytype_id || (txn.type === 'expense' ? 2 : 1),
    //     //     type: txn.type === 'expense' ? 'payment' : 'receipt'
    //     // };

    //     // // First set the transaction
    //     // await setSelectedBankTxn(modalTxn);

    //     // // Then open modal
    //     // setShowMatchModal(true);
    //      if (txn.type === 'advance' || txn.type === 'capital') {
    //     // For advance transactions, we need to fetch the ledger entry details
    //     await handleAdvanceReconciliation(txn);
    // } else {
    //     // Regular income/expense transaction
    //     const modalTxn = {
    //         ...txn,
    //         type: txn.type === 'expense' ? 'payment' : 'receipt'
    //     };
    //     await setSelectedBankTxn(modalTxn);
    //     setShowMatchModal(true);
    // }
    // };

    const handleReconcileClick = async (txn) => {
        console.log('🔍 Transaction details for reconciliation:', {
            id: txn.id,
            type: txn.type,
            entrytype_id: txn.entrytype_id,
            entry_id: txn.entry_id,
            entryId: txn.entryId,
            entryID: txn.entryID,
            rawTransactionType: txn._rawTransaction?.type,
            isAdvance: txn._rawTransaction?.type === 'advance' || txn.type === 'advance'
        });

        // FIXED: Only treat as advance if raw type is 'advance'
        const transactionType = txn._rawTransaction?.type || txn.type;

        // REMOVE the || txn.entryID condition - that's what's causing all transactions to be advances
        if (transactionType === 'advance') {
            console.log('✅ This is an advance transaction');
            await handleAdvanceReconciliation(txn);
        } else if (transactionType === 'contra') {
            console.log('✅ This is a contra transaction');
            const modalTxn = {
                ...txn,
                type: 'contra',
                is_contra: true
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        } else if (transactionType === 'vat_payable') {
            console.log('✅ This is a VAT payable transaction');
            const modalTxn = {
                ...txn,
                type: 'vat_payable',
                is_vat_payable: true
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        } else if (transactionType === 'payroll' || txn.is_payroll) {
            console.log('✅ This is a payroll transaction');
            const modalTxn = {
                ...txn,
                type: 'payroll',
                is_payroll: true
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        } else if (transactionType === 'expense' || txn.entrytype_id === 2) {
            // Regular expense/payment transaction
            console.log('✅ This is a payment transaction');
            const modalTxn = {
                ...txn,
                type: 'payment'  // Explicitly set to payment
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        } else if (transactionType === 'income' || txn.entrytype_id === 1) {
            // Regular income/receipt transaction
            console.log('✅ This is a receipt transaction');
            const modalTxn = {
                ...txn,
                type: 'receipt'  // Explicitly set to receipt
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        } else if (transactionType === 'capital') {
            console.log('This is a capital transaction');
            // Capital transactions might need special handling
            // They could be treated similar to advance payments
            await handleCapitalReconciliation(txn);
        } else if (transactionType === 'withdrawal') {
            console.log('✅ This is a withdrawal transaction');
            await handleWithdrawalReconciliation(txn);
        } else if (['bank_guarantee', 'bank_guarantee_released', 'bank_guarantee_charges', 'bad_debt_written_off'].includes(transactionType)) {
            console.log(`✅ This is a ${transactionType} transaction`);
            // Reuse advance handler as it fetches ledger details which is what we need for special entries
            await handleAdvanceReconciliation(txn);
        } else {
            console.log('⚠️ Unknown transaction type:', transactionType);
            // Handle as regular transaction
            const modalTxn = {
                ...txn,
                type: txn.entrytype_id === 2 ? 'payment' : 'receipt'
            };
            await setSelectedBankTxn(modalTxn);
            setShowMatchModal(true);
        }
    };

    const handleAdvanceReconciliation = async (txn) => {
        try {
            // Get entry ID from entryID property
            const entryId = txn.entryID || txn.entry_id || txn.entryId;

            console.log('🔍 Advance reconciliation details:', {
                transactionId: txn.id,
                transactionType: txn.type,
                rawTransactionType: txn._rawTransaction?.type,
                entryId: entryId,
                isActuallyAdvance: txn._rawTransaction?.type === 'advance' || txn.type === 'advance'
            });

            // Check if it's REALLY an advance transaction
            const isActuallyAdvance = txn._rawTransaction?.type === 'advance' || txn.type === 'advance';

            if (!isActuallyAdvance) {
                console.log('❌ Not an advance transaction, treating as regular');
                // If it's not actually an advance, treat it as regular
                const modalTxn = {
                    ...txn,
                    type: txn.entrytype_id === 2 ? 'payment' : 'receipt'
                };
                await setSelectedBankTxn(modalTxn);
                setShowMatchModal(true);
                return;
            }

            if (entryId) {
                console.log(`🔍 Calling fetchLedgerEntryById with ID: ${entryId}`);
                const entryDetails = await fetchLedgerEntryById(entryId);

                if (entryDetails) {
                    console.log('✅ Entry details fetched:', entryDetails);
                    // Show modal with advance details
                    setSelectedBankTxn({
                        ...txn,
                        entryDetails: entryDetails,
                        isAdvance: true,
                        type: 'advance' // Explicitly set type to advance
                    });
                    setShowMatchModal(true);
                } else {
                    console.log('❌ No entry details returned from API');
                    alert('Unable to fetch advance transaction details');
                }
            } else {
                console.log('❌ No entry ID found for this advance transaction');
                alert('No entry ID found for this advance transaction');
            }
        } catch (error) {
            console.error('❌ Error handling advance reconciliation:', error);
            alert('Error processing advance transaction');
        }
    };
    const handleCapitalReconciliation = async (txn) => {
        try {
            const entryId = txn.entryID || txn.entry_id || txn.entryId;

            console.log('🔍 Capital reconciliation details:', {
                transactionId: txn.id,
                transactionType: txn.type,
                originalType: txn._rawTransaction?.type,
                entryId: entryId,
                isCapital: true
            });

            if (entryId) {
                console.log(`🔍 Fetching ledger entry for capital transaction ID: ${entryId}`);
                const entryDetails = await fetchLedgerEntryById(entryId);

                if (entryDetails) {
                    console.log('✅ Capital entry details fetched:', entryDetails);
                    // Show modal with capital details
                    setSelectedBankTxn({
                        ...txn,
                        entryDetails: entryDetails,
                        isCapital: true, // Mark as capital
                        type: 'capital' // Explicitly set type to capital
                    });
                    setShowMatchModal(true);
                } else {
                    console.log('❌ No entry details returned for capital transaction');
                    alert('Unable to fetch capital transaction details');
                }
            } else {
                console.log('❌ No entry ID found for this capital transaction');
                alert('No entry ID found for this capital transaction');
            }
        } catch (error) {
            console.error('❌ Error handling capital reconciliation:', error);
            alert('Error processing capital transaction');
        }
    };

    const handleWithdrawalReconciliation = async (txn) => {
        try {
            const entryId = txn.entryID || txn.entry_id || txn.entryId;

            console.log('🔍 Withdrawal reconciliation details:', {
                transactionId: txn.id,
                transactionType: txn.type,
                originalType: txn._rawTransaction?.type,
                entryId: entryId,
                isWithdrawal: true
            });

            if (entryId) {
                console.log(`🔍 Fetching ledger entry for withdrawal transaction ID: ${entryId}`);
                const entryDetails = await fetchLedgerEntryById(entryId);

                if (entryDetails) {
                    console.log('✅ Withdrawal entry details fetched:', entryDetails);
                    // Show modal with withdrawal details
                    setSelectedBankTxn({
                        ...txn,
                        entryDetails: entryDetails,
                        isWithdrawal: true, // Mark as withdrawal
                        type: 'withdrawal' // Explicitly set type to withdrawal
                    });
                    setShowMatchModal(true);
                } else {
                    console.log('❌ No entry details returned for withdrawal transaction');
                    alert('Unable to fetch withdrawal transaction details');
                }
            } else {
                console.log('❌ No entry ID found for this withdrawal transaction');
                alert('No entry ID found for this withdrawal transaction');
            }
        } catch (error) {
            console.error('❌ Error handling withdrawal reconciliation:', error);
            alert('Error processing withdrawal transaction');
        }
    };
    const fetchLedgerEntryById = async (entryId) => {
        try {
            const response = await axios.get(`${Api}/api/entries/getEntryById`, {
                headers: { 'x-api-key': apiKey },
                params: { entry_id: entryId }
            });

            return response.data?.entry || null;
        } catch (error) {
            console.error('Error fetching ledger entry:', error);
            return null;
        }
    };
    const fetchJournalEntryDetails = async (journalId) => {
        try {
            const res = await axios.get(`${Api}/api/entries/getEntryById`, {
                headers: { 'x-api-key': apiKey },
                params: { entry_id: journalId }
            });
            return res.data?.entry || null;
        } catch (err) {
            console.error('Error fetching journal entry:', err);
            return null;
        }
    };
    const ReconciliationMatchModal = ({ show, onHide, bankTxn }) => {
        const [localBankTxn, setLocalBankTxn] = useState(null);
        const [loadingReconcile, setLoadingReconcile] = useState(false);
        const [differenceReason, setDifferenceReason] = useState('');

        // Sync bankTxn prop with local state
        useEffect(() => {
            if (bankTxn) {
                setLocalBankTxn(bankTxn);
            }
        }, [bankTxn, show]);

        // Early return if no data
        if (!localBankTxn) {
            return (
                <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '950px' }}>
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>Loading...</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <div className="text-center p-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading transaction details...</p>
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>
            );
        }

        console.log("banktxn", bankTxn);


        const isAdvance = bankTxn?.type === 'advance' || bankTxn?._rawTransaction?.type === 'advance';
        const isAdvancePayment = isAdvance;
        const isCapital = bankTxn?.type === 'capital' || bankTxn?._rawTransaction?.type === 'capital';
        const isCapitalTransaction = isCapital;
        const isContra = bankTxn?.type === 'contra' || bankTxn?._rawTransaction?.type === 'contra';
        const isWithdrawal = bankTxn?.type === 'withdrawal' || bankTxn?._rawTransaction?.type === 'withdrawal' || bankTxn?.is_withdrawal;
        const isVatPayable = bankTxn?.type === 'vat_payable' || bankTxn?._rawTransaction?.type === 'vat_payable';
        const isBankGuarantee = bankTxn?.type === 'bank_guarantee' || bankTxn?._rawTransaction?.type === 'bank_guarantee' || bankTxn?.is_bank_guarantee;
        const isBankGuaranteeReleased = bankTxn?.type === 'bank_guarantee_released' || bankTxn?._rawTransaction?.type === 'bank_guarantee_released' || bankTxn?.is_bank_guarantee_released;
        const isBankGuaranteeCharges = bankTxn?.type === 'bank_guarantee_charges' || bankTxn?._rawTransaction?.type === 'bank_guarantee_charges' || bankTxn?.is_bank_guarantee_charges;
        const isBadDebtWrittenOff = bankTxn?.type === 'bad_debt_written_off' || bankTxn?._rawTransaction?.type === 'bad_debt_written_off' || bankTxn?.is_bad_debt_written_off;
        const isSpecialType = isBankGuarantee || isBankGuaranteeReleased || isBankGuaranteeCharges || isBadDebtWrittenOff;


        // Transaction type determination
        const isReceipt = bankTxn?.entrytype_id === 1 || bankTxn?.type === 'receipt';
        const isPayment = bankTxn?.entrytype_id === 2 || bankTxn?.type === 'payment';
        // const transactionType = isPayment ? 'Payment' : 'Receipt';
        // const transactionType = isAdvancePayment ? 'Advance' : isPayment ? 'Payment' : 'Receipt';
        // const transactionType = isAdvancePayment ? 'Advance' :
        //     isCapitalTransaction ? 'Capital' :
        //         isPayment ? 'Payment' : 'Receipt';
        const isPayroll = bankTxn?.type === 'payroll' || bankTxn?._rawTransaction?.type === 'payroll' || bankTxn?.is_payroll;

        const specialTypeLabel = isBankGuarantee ? 'Bank Guarantee' :
            isBankGuaranteeReleased ? 'BG Released' :
                isBankGuaranteeCharges ? 'BG Charges' :
                    isBadDebtWrittenOff ? 'Bad Debt Written Off' : null;

        const advanceLabel = bankTxn?.advance_type === 'client' ? 'Client Advance' :
            bankTxn?.advance_type === 'vendor' ? 'Vendor Advance' : 'Advance';

        const transactionType = isAdvancePayment ? advanceLabel :
            isCapitalTransaction ? 'Capital' :
                isContra ? 'Contra' :
                    isPayroll ? 'Payroll' :
                        isWithdrawal ? 'Withdrawal' :
                            specialTypeLabel ? specialTypeLabel :
                                isPayment ? 'Payment' : 'Receipt';


        // Get vendor/client name
        const vendorClientName = isPayment
            ? bankTxn?.vendor?.name || 'N/A'
            : bankTxn?.client?.name || 'N/A';
        const deductionAmount = parseFloat(bankTxn?.deduction_amount || 0);

        // In the ReconciliationMatchModal component, update the calculateVoucherTotals function:
        const calculateVoucherTotals = () => {
            // Use the voucher_totals provided in the transaction data
            if (isAdvancePayment) {
                const bankAmount = parseFloat(bankTxn?.amount || 0);

                return {
                    totalAmount: bankAmount,
                    totalPaid: bankAmount,
                    totalBalance: 0,
                    bankAmount: bankAmount,
                    expectedBankAmount: bankAmount,
                    difference: 0,
                    isExactMatch: true,
                    isAdvance: true
                };
            }
            if (isCapitalTransaction) {
                const bankAmount = parseFloat(bankTxn?.amount || 0);

                return {
                    totalAmount: bankAmount,
                    totalPaid: bankAmount,
                    totalBalance: 0,
                    bankAmount: bankAmount,
                    expectedBankAmount: bankAmount,
                    difference: 0,
                    isExactMatch: true,
                    isCapital: true
                };
            }
            if (isWithdrawal || isSpecialType) {
                const bankAmount = parseFloat(bankTxn?.amount || 0);

                return {
                    totalAmount: bankAmount,
                    totalPaid: bankAmount,
                    totalBalance: 0,
                    bankAmount: bankAmount,
                    expectedBankAmount: bankAmount,
                    difference: 0,
                    isExactMatch: true,
                    isSpecial: true
                };
            }
            if (isContra) {
                const bankAmount = parseFloat(bankTxn?.amount || 0);
                return {
                    totalAmount: bankAmount,
                    totalPaid: bankAmount,
                    totalBalance: 0,
                    bankAmount: bankAmount,
                    expectedBankAmount: bankAmount,
                    difference: 0,
                    isExactMatch: true,
                    isContra: true
                };
            }
            if (isVatPayable) {
                const bankAmount = parseFloat(bankTxn?.amount || 0);
                return {
                    totalAmount: bankAmount,
                    totalPaid: bankAmount,
                    totalBalance: 0,
                    bankAmount: bankAmount,
                    expectedBankAmount: bankAmount,
                    difference: 0,
                    isExactMatch: true,
                    isVatPayable: true
                };
            }
            const voucherTotals = bankTxn?.voucher_totals || {
                invoice_items_total: 0,
                invoice_items_paid: 0,
                invoice_items_balance: 0,
                expense_items_total: 0,
                expense_items_paid: 0,
                expense_items_balance: 0,
                total_voucher_amount: 0,
                total_voucher_paid: 0,
                total_voucher_balance: 0
            };

            // Calculate expected bank amount
            let expectedBankAmount = voucherTotals.total_voucher_paid;
            if (isReceipt && deductionAmount > 0) {
                expectedBankAmount = voucherTotals.total_voucher_paid - deductionAmount;
            }

            const bankAmount = parseFloat(bankTxn?.amount || 0);
            const difference = expectedBankAmount - bankAmount;
            const isExactMatch = Math.abs(difference) < 0.01;

            // Process invoice details for display
            const invoiceMap = {};
            const invoiceDetails = [];

            if (bankTxn?.transaction_invoice_items &&
                Array.isArray(bankTxn.transaction_invoice_items) &&
                bankTxn.transaction_invoice_items.length > 0) {

                bankTxn.transaction_invoice_items.forEach(item => {
                    const invoiceId = item.invoice_id;
                    const invoice = item.invoice;

                    // Get invoice total from the invoice object
                    const invoiceTotal = parseFloat(invoice?.amount || invoice?.total_amount || item.amount || 0);

                    // Get paid amount from this transaction
                    const currentPayment = parseFloat(item.paid_amount || 0);

                    // Get balance from the item
                    const balanceAmount = parseFloat(item.balance_amount || 0);

                    // Calculate cumulative paid (invoice total - balance)
                    const cumulativePaid = invoiceTotal - balanceAmount;

                    if (!invoiceMap[invoiceId]) {
                        invoiceMap[invoiceId] = {
                            invoice_id: invoiceId,
                            invoice_ref: invoice?.ref_no || `INV-${invoiceId}`,
                            total_amount: invoiceTotal,
                            cumulative_paid: cumulativePaid,
                            current_payment: currentPayment,
                            cumulative_balance: balanceAmount,
                            is_fully_paid: item.is_fully_paid || Math.abs(balanceAmount) < 0.01,
                            items: []
                        };
                    }

                    invoiceMap[invoiceId].items.push({
                        transaction_item_id: item.id,
                        paid_amount: currentPayment,
                        balance_amount: balanceAmount,
                        paid_percentage: parseFloat(item.paid_percentage || 0),
                        is_fully_paid: item.is_fully_paid,
                        cumulative_paid_percentage: invoiceTotal > 0 ?
                            ((cumulativePaid / invoiceTotal) * 100).toFixed(1) : '0.0'
                    });
                });

                invoiceDetails.push(...Object.values(invoiceMap));
            }

            // Process expense details for display with currency conversion
            const poMap = {};
            const USD_AED_RATE = Number(process.env.REACT_APP_EXCHANGE_RATE_USD_AED) || 3.692;

            const convertAmount = (amount, from, to) => {
                const value = Number(amount);
                if (!value || from === to) return value;

                if (from === 'USD' && to === 'AED') {
                    return value * USD_AED_RATE;
                }

                if (from === 'AED' && to === 'USD') {
                    return value / USD_AED_RATE;
                }

                return value;
            };

            if (bankTxn?.transaction_expense_items &&
                Array.isArray(bankTxn.transaction_expense_items) &&
                bankTxn.transaction_expense_items.length > 0) {

                bankTxn.transaction_expense_items.forEach(item => {
                    // Get PO details
                    const po = item.expense?.purchase_order || item.purchase_order || item.expense;
                    const poId = item.po_id;

                    // Get currency information
                    const poCurrency = po?.vendor?.currency || 'AED'; // PO currency from vendor
                    const expenseCurrency = item.expense?.vendor_currency || 'AED'; // Expense currency
                    const bankCurrency = bankTxn?.currency || 'AED'; // Bank transaction currency

                    if (poId) {
                        if (!poMap[poId]) {
                            // Convert PO total to bank currency for display
                            const poTotalAmount = parseFloat(po?.amount || po?.total_amount || 0);
                            const convertedPOTotal = convertAmount(poTotalAmount, poCurrency, bankCurrency);

                            poMap[poId] = {
                                po_id: poId,
                                po_reference_no: po?.ref_no || item.po_reference_no || 'N/A',
                                po_total_amount: poTotalAmount,
                                po_currency: poCurrency,
                                converted_po_total: convertedPOTotal,
                                po_object: po,
                                expenses: [],
                                total_paid: 0,
                                total_balance: 0,
                                converted_total_paid: 0,
                                converted_total_balance: 0
                            };
                        }

                        // Convert expense amounts to bank currency
                        const expenseAmount = parseFloat(item.amount || 0);
                        const expensePaid = parseFloat(item.paid_amount || 0);
                        const expenseBalance = parseFloat(item.balance_amount || 0);

                        const convertedExpenseAmount = convertAmount(expenseAmount, expenseCurrency, bankCurrency);
                        const convertedExpensePaid = convertAmount(expensePaid, expenseCurrency, bankCurrency);
                        const convertedExpenseBalance = convertAmount(expenseBalance, expenseCurrency, bankCurrency);

                        poMap[poId].expenses.push({
                            expense_id: item.expense_id,
                            expense_no: item.expense?.invoice_no || `EXP-${item.expense_id}`,
                            amount: expenseAmount,
                            paid_amount: expensePaid,
                            balance_amount: expenseBalance,
                            converted_amount: convertedExpenseAmount,
                            converted_paid: convertedExpensePaid,
                            converted_balance: convertedExpenseBalance,
                            is_fully_paid: item.is_fully_paid,
                            paid_percentage: parseFloat(item.paid_percentage || 0),
                            expense_object: item.expense,
                            expense_currency: expenseCurrency,
                            bank_currency: bankCurrency
                        });

                        // Track totals in original currency
                        poMap[poId].total_paid += expensePaid;
                        poMap[poId].total_balance += expenseBalance;

                        // Track totals in converted currency
                        poMap[poId].converted_total_paid += convertedExpensePaid;
                        poMap[poId].converted_total_balance += convertedExpenseBalance;
                    }
                });
            }

            return {
                // Use the voucher_totals from transaction data
                totalInvoiceAmount: voucherTotals.invoice_items_total,
                totalInvoicePaid: voucherTotals.invoice_items_paid,
                totalInvoiceBalance: voucherTotals.invoice_items_balance,
                totalExpenseAmount: voucherTotals.expense_items_total,
                totalExpensePaid: voucherTotals.expense_items_paid,
                totalExpenseBalance: voucherTotals.expense_items_balance,
                totalAmount: voucherTotals.total_voucher_amount,
                totalPaid: voucherTotals.total_voucher_paid,
                totalBalance: voucherTotals.total_voucher_balance,
                expectedBankAmount,
                bankAmount,
                difference,
                isExactMatch,
                differencePercentage: voucherTotals.total_voucher_paid > 0 ?
                    ((difference / voucherTotals.total_voucher_paid) * 100).toFixed(2) : '0.00',
                poDetails: Object.values(poMap),
                invoiceDetails: invoiceDetails,
                bankCurrency: bankTxn?.currency || 'AED'
            };
        };

        // Update the PO table display in the modal to show currency information:


        const voucherTotals = calculateVoucherTotals();

        // Function to get PO total amount from relationship
        const getPOTotalAmount = (poId) => {
            const po = voucherTotals.poDetails.find(p => p.po_id === poId);
            return po ? po.po_total_amount : 0;
        };

        // Function to get PO balance (PO total - total paid for this PO in voucher)
        const getPOBalance = (poId) => {
            const po = voucherTotals.poDetails.find(p => p.po_id === poId);
            if (!po) return 0;

            return po.po_total_amount - po.total_paid;
        };

        // Function to get overall PO balance (including payments from other transactions)
        const getOverallPOBalance = (poId) => {
            const po = voucherTotals.poDetails.find(p => p.po_id === poId);
            if (!po) return 0;

            // If we have the PO object with amount, use it
            if (po.po_object?.amount || po.po_object?.total_amount) {
                const poTotal = parseFloat(po.po_object.amount || po.po_object.total_amount);

                // Calculate total paid from ALL transactions (you would need to fetch this from API)
                // For now, we'll show the balance from this voucher only
                return poTotal - po.total_paid;
            }

            return po.po_total_amount - po.total_paid;
        };

        const handleReconcile = async () => {
            setLoadingReconcile(true);
            try {
                let success = false;
                // Identify the ERP transaction ID
                const erpId = bankTxn.entryID || bankTxn.entry_id || bankTxn.entryId || bankTxn.id;

                if (isAdvancePayment) {
                    success = await reconcileTransaction(bankTxn, {
                        id: erpId,
                        type: 'advance',
                        amount: bankTxn.amount
                    });
                }
                else if (isPayroll) {
                    success = await reconcileTransaction(bankTxn, {
                        id: erpId,
                        type: 'payroll',
                        amount: bankTxn.amount,
                        payroll_details: {
                            net_amount: bankTxn.net_payroll_amount,
                            wps_charge: bankTxn.wps_charge,
                            vat_amount: bankTxn.payroll_vat || bankTxn.wps_vat,
                            total_amount: bankTxn.bank_amount || bankTxn.amount
                        }
                    });
                }
                else if (isCapitalTransaction) {
                    success = await reconcileTransaction(bankTxn, { id: erpId, type: 'capital', amount: bankTxn.amount });
                }
                else if (isWithdrawal) {
                    success = await reconcileTransaction(bankTxn, { id: erpId, type: 'withdrawal', amount: bankTxn.amount });
                }
                else if (isContra) {
                    success = await reconcileTransaction(bankTxn, { id: erpId, type: 'contra', amount: bankTxn.amount });
                }
                else if (isVatPayable) {
                    success = await reconcileTransaction(bankTxn, { id: erpId, type: 'vat_payable', amount: bankTxn.amount });
                }
                else if (isSpecialType) {
                    success = await reconcileTransaction(bankTxn, {
                        id: erpId,
                        type: bankTxn.type || bankTxn._rawTransaction?.type || 'special',
                        amount: bankTxn.amount,
                    });
                }
                else {
                    // Regular Voucher matching logic (Receipts/Payments)
                    let itemIds = [];
                    let itemType = isPayment ? 'expense' : 'invoice';

                    if (isPayment && bankTxn?.transaction_expense_items?.length > 0) {
                        itemIds = bankTxn.transaction_expense_items.map(item => item.expense_id).filter(id => id);
                    } else if (isReceipt && bankTxn?.transaction_invoice_items?.length > 0) {
                        itemIds = bankTxn.transaction_invoice_items.map(item => item.invoice_id).filter(id => id);
                    }

                    if (itemIds.length === 0) {
                        if (isPayment && bankTxn?.expense_id) {
                            itemIds = bankTxn.expense_id.split(',').filter(id => id.trim());
                        } else if (isReceipt && bankTxn?.invoice_id) {
                            itemIds = [bankTxn.invoice_id.toString()];
                        }
                    }

                    if (itemIds.length === 0) throw new Error('No voucher items found for reconciliation');

                    if (itemIds.length === 1) {
                        success = await reconcileTransaction(bankTxn, { id: itemIds[0] });
                    } else {
                        success = await reconcileMultipleTransactions(bankTxn, itemIds.map(id => ({ id })));
                    }
                }

                if (success) {
                    // 1. Close modal immediately to avoid re-render issues
                    onHide();

                    // 2. Refresh the bank transaction list in the parent
                    if (typeof fetchBankTransactions === 'function') {
                        fetchBankTransactions();
                    }

                    // 3. Inform the user
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `Reconciliation successful`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error('Reconciliation failed. Please try again.');
                }
            } catch (error) {
                console.error("Reconciliation error:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Reconciliation failed.'
                });
            } finally {
                setLoadingReconcile(false);
            }
        };



        return (
            <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '950px' }}>
                <Offcanvas.Header closeButton className="border-bottom py-3">
                    <Offcanvas.Title className="w-100">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h5 className="mb-1 fw-bold">Bank Reconciliation</h5>
                                <div className="d-flex align-items-center gap-3">
                                    <span className="badge bg-secondary">
                                        {transactionType} Voucher
                                    </span>
                                    <span className="text-muted">
                                        Voucher ID: <strong>{bankTxn?.transaction_voucher_id || ''}</strong>
                                    </span>
                                    <span className="text-muted">
                                        Date: <strong>{formatDateForDisplay(bankTxn?.date)}</strong>
                                    </span>
                                </div>
                            </div>
                            <div className="text-end">
                                <div className="fs-5 fw-bold d-flex align-items-center justify-content-end">
                                    {(() => {
                                        const rawType = bankTxn?._rawTransaction?.type || bankTxn?.type;
                                        const isContra = rawType === 'contra';
                                        const isMoneyIn = (rawType === 'income' || (isContra && bankTxn?.isSelectedTo) || bankTxn?.entrytype_id === 1);
                                        const isMoneyOut = (rawType === 'expense' || rawType === 'payroll' || rawType === 'vat_payable' || rawType === 'withdrawal' || (isContra && bankTxn?.isSelectedFrom) || bankTxn?.entrytype_id === 2);

                                        if (isMoneyIn) return <FiArrowDownLeft className="text-success me-2" />;
                                        if (isMoneyOut) return <FiArrowUpRight className="text-danger me-2" />;
                                        return null;
                                    })()}
                                    <span className="text-primary">{bankTxn?.amount || '0.00'} {bankTxn?.currency || ''}</span>
                                </div>
                                <div className="text-muted small">Bank Transaction</div>
                            </div>
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-4">
                    {/* Section 1: Transaction Information */}
                    <div className="mb-4">
                        {isContra && (
                            <div className="mb-3">
                                {bankTxn.isSelectedTo ? (
                                    <div className="alert alert-soft-success border-0 d-flex align-items-center mb-0">
                                        <FiArrowDownLeft className="fs-18 me-2" />
                                        <span>This is a <strong>Funds Transfer Receipt</strong> (Debit) to the current account.</span>
                                    </div>
                                ) : bankTxn.isSelectedFrom ? (
                                    <div className="alert alert-soft-danger border-0 d-flex align-items-center mb-0">
                                        <FiArrowUpRight className="fs-18 me-2" />
                                        <span>This is a <strong>Funds Transfer Payment</strong> (Credit) from the current account.</span>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {isWithdrawal && (
                            <div className="mb-3">
                                <div className="alert alert-soft-danger border-0 d-flex align-items-center mb-0">
                                    <FiArrowUpRight className="fs-18 me-2" />
                                    <span>This is a <strong>Cash Withdrawal</strong> (Credit) from the current account.</span>
                                </div>
                            </div>
                        )}
                        <h6 className="mb-3 fw-bold border-bottom pb-2">Transaction Information</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <table className="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="40%" className="text-muted">Transaction Type:</td>
                                            <td className="fw-medium">{transactionType}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">{
                                                isContra ? 'From Bank:' :
                                                    isWithdrawal ? 'Withdraw From:' :
                                                        isVatPayable ? 'Tax Authority:' :
                                                            isPayment ? 'Vendor:' : 'Client:'
                                            }</td>
                                            <td className="fw-medium">{
                                                isContra ? (bankTxn?.from_bank_name || 'N/A') :
                                                    isWithdrawal ? (bankTxn?.accounts?.name || 'N/A') :
                                                        isVatPayable ? '' :
                                                            (bankTxn?.vendor?.name || bankTxn?.client?.name || 'N/A')
                                            }</td>
                                        </tr>
                                        {isContra && (
                                            <tr>
                                                <td className="text-muted">To Bank:</td>
                                                <td className="fw-medium">{bankTxn?.to_bank_name || 'N/A'}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="text-muted">Bank Reference:</td>
                                            <td className="fw-medium">{bankTxn?.reference_no || ''}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-md-6">
                                <table className="table table-sm table-borderless">
                                    <tbody>
                                        <tr>
                                            <td width="40%" className="text-muted">Transaction Date:</td>
                                            <td className="fw-medium">{formatDateForDisplay(bankTxn?.date)}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Bank Account:</td>
                                            <td className="fw-medium">{bankTxn?.accounts?.name || ''}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Currency:</td>
                                            <td className="fw-medium">{bankTxn?.currency || ''}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {bankTxn && (
                            <div className="mt-3">
                                <small className="text-muted d-block mb-2">Related Project(s):</small>
                                {renderProjectBadges(bankTxn)}
                            </div>
                        )}
                    </div>

                    {/* Reversal Details Section */}
                    {(bankTxn?.reconciliation_status === 'reversed' || bankTxn?.is_reversal_row) && (
                        <div className="mb-4 bg-soft-secondary p-3 rounded">
                            <h6 className="mb-3 fw-bold border-bottom pb-2 text-secondary">
                                <FiRefreshCw className="me-2" />
                                Reversal Information
                            </h6>
                            <div className="row g-3">
                                {(() => {
                                    // Calculate amounts logic
                                    // Ledger Amount (Total hitting the bank)
                                    const ledgerTotal = parseFloat(bankTxn.amount || 0);

                                    // Principal Reversal Amount (from DB record)
                                    // If reverse_amount is 0/null, assume full amount was reversed (fallback to transaction amount)
                                    // But be careful: if it's a partial reversal, this logic needs to hold.
                                    const dbPrincipal = parseFloat(bankTxn.reverse_amount || 0);
                                    const originalAmount = parseFloat(bankTxn._rawTransaction?.amount || bankTxn.amount || 0);

                                    // Use dbPrincipal if available, otherwise use originalAmount as the intended reversal principal
                                    // Fallback to ledgerTotal only if originalAmount is missing (unlikely)
                                    const principal = dbPrincipal > 0 ? dbPrincipal : (originalAmount > 0 ? originalAmount : ledgerTotal);

                                    // Charges from DB
                                    const dbCharges = parseFloat(bankTxn.reverse_service_charge || 0) + parseFloat(bankTxn.reverse_vat || 0);

                                    // Implied Charges (Difference between Ledger Total and Principal)
                                    // Ideally LedgerTotal = Principal + Charges
                                    // So ImpliedCharges = LedgerTotal - Principal
                                    const impliedCharges = Math.max(0, ledgerTotal - principal);

                                    // Use DB charges if present, otherwise use implied charges from ledger diff
                                    const finalCharges = dbCharges > 0 ? dbCharges : impliedCharges;

                                    // Debug logging
                                    console.log('=== REVERSAL CHARGE CALCULATION ===');
                                    console.log('bankTxn:', bankTxn);
                                    console.log('ledgerTotal (bankTxn.amount):', ledgerTotal);
                                    console.log('dbPrincipal (bankTxn.reverse_amount):', dbPrincipal);
                                    console.log('originalAmount (bankTxn._rawTransaction?.amount):', originalAmount);
                                    console.log('principal (calculated):', principal);
                                    console.log('dbCharges:', dbCharges);
                                    console.log('impliedCharges (ledgerTotal - principal):', impliedCharges);
                                    console.log('finalCharges:', finalCharges);
                                    console.log('===================================');

                                    return (
                                        <>
                                            <div className="col-md-6">
                                                <table className="table table-sm table-borderless mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-muted">Reversal Date:</td>
                                                            <td className="fw-bold">{bankTxn.reversal_date ? formatDateForDisplay(bankTxn.reversal_date) : 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Reason:</td>
                                                            <td>{bankTxn.reversal_reason || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-muted">Reversed Principal:</td>
                                                            <td className="fw-bold">{formatUSCurrency(principal)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="col-md-6">
                                                <table className="table table-sm table-borderless mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-muted">Bank Charges & VAT:</td>
                                                            <td className="text-black">
                                                                {finalCharges > 0 ? formatUSCurrency(finalCharges) : '0.00'}
                                                                {finalCharges > 0 && dbCharges === 0 && <small className="text-muted ms-1">(from ledger)</small>}
                                                            </td>
                                                        </tr>
                                                        {bankTxn.reverse_deduction > 0 && (
                                                            <tr>
                                                                <td className="text-muted">Deduction:</td>
                                                                <td className="text-danger">{formatUSCurrency(bankTxn.reverse_deduction)}</td>
                                                            </tr>
                                                        )}
                                                        <tr className="bg-white border-top">
                                                            <td className="fw-bold text-success ps-2">Total Amount:</td>
                                                            <td className="fw-bold text-success">
                                                                {formatUSCurrency(ledgerTotal)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Section 2: Voucher Details */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-bold border-bottom pb-2">Voucher Details</h6>
                            <div className="text-end">
                                <div className="fw-bold text-success">Total Paid: {voucherTotals.totalPaid.toFixed(2)}</div>
                                <div className="text-muted small">Voucher ID: {bankTxn?.transaction_voucher_id}</div>
                            </div>
                        </div>

                        {/* Invoices Section */}
                        {/* {bankTxn?.transaction_invoice_items?.length > 0 && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fs-6">Invoices</h6>
                                    <span className="badge bg-secondary">
                                        {bankTxn.transaction_invoice_items.length} item(s)
                                    </span>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-sales-gradient">
                                            <tr>
                                                <th width="25%">Invoice No</th>
                                                <th width="15%" className="text-end">Amount</th>
                                                <th width="15%" className="text-end">Paid</th>
                                                <th width="15%" className="text-end">Balance</th>
                                                <th width="15%" className="text-center">Status</th>
                                                <th width="15%" className="text-center">% Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bankTxn.transaction_invoice_items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {item.invoice?.ref_no || `INV-${item.invoice_id}`}
                                                       
                                                    </td>
                                                    <td className="text-end">{formatUSCurrency(item.amount)}</td>
                                                    <td className="text-end text-success">{formatUSCurrency(item.paid_amount)}</td>
                                                    <td className="text-end text-warning">{formatUSCurrency(item.balance_amount)}</td>
                                                    <td className="text-center">
                                                        <span className={`badge ${item.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                            {item.is_fully_paid ? 'Paid' : 'Partial'}
                                                        </span>
                                                    </td>
                                                    <td className="text-center fw-bold">
                                                        {parseFloat(item.paid_percentage || 0).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="table-secondary fw-bold">
                                                <td>Invoice Totals</td>
                                                <td className="text-end">{formatUSCurrency(voucherTotals.totalInvoiceAmount)}</td>
                                                <td className="text-end text-success">{formatUSCurrency(voucherTotals.totalInvoicePaid)}</td>
                                                <td className="text-end text-warning">{formatUSCurrency(voucherTotals.totalInvoiceBalance)}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-dark">
                                                        {voucherTotals.totalInvoicePaid > 0 ? 'Paid' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    {voucherTotals.totalInvoiceAmount > 0 ?
                                                        ((voucherTotals.totalInvoicePaid / voucherTotals.totalInvoiceAmount) * 100).toFixed(1) : 0}%
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )} */}

                        {/* Invoices Section */}
                        {/* {(bankTxn?.transaction_invoice_items?.length > 0 || voucherTotals.invoiceDetails?.length > 0) && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fs-6">Invoices</h6>
                                    <span className="badge bg-secondary">
                                        {voucherTotals.invoiceDetails.length} invoice(s)
                                    </span>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="table-sales-gradient">
                                            <tr>
                                                <th width="25%">Invoice No</th>
                                                <th width="15%" className="text-end">Total Amount</th>
                                                <th width="15%" className="text-end">Cumulative Paid</th>
                                                <th width="15%" className="text-end">Current Payment</th>
                                                <th width="15%" className="text-end">Balance</th>
                                                <th width="15%" className="text-center">% Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {voucherTotals.invoiceDetails.map((invoice, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {invoice.invoice_ref}
                                                        <div className="text-muted small">
                                                            Invoice ID: {invoice.invoice_id}
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-bold">
                                                        {formatUSCurrency(invoice.total_amount)}
                                                    </td>
                                                    <td className="text-end text-success">
                                                        {formatUSCurrency(invoice.cumulative_paid)}
                                                        {invoice.items.length > 1 && (
                                                            <div className="text-muted small">
                                                                ({invoice.items.length} payments)
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        {formatUSCurrency(invoice.items[invoice.items.length - 1]?.paid_amount || 0)}
                                                        <div className="text-muted small">
                                                            This transaction
                                                        </div>
                                                    </td>
                                                    <td className="text-end text-warning">
                                                        {formatUSCurrency(invoice.cumulative_balance)}
                                                        <div className="text-muted small">
                                                            Remaining
                                                        </div>
                                                    </td>
                                                    <td className="text-center fw-bold">
                                                        <span className={`badge ${invoice.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                            {invoice.total_amount > 0 ?
                                                                ((invoice.cumulative_paid / invoice.total_amount) * 100).toFixed(1) : 0}%
                                                        </span>
                                                        <div className="text-muted small">
                                                            Cumulative
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}

                                            <tr className="table-secondary fw-bold">
                                                <td>Invoice Totals</td>
                                                <td className="text-end">{formatUSCurrency(voucherTotals.totalInvoiceAmount)}</td>
                                                <td className="text-end text-success">
                                                    {formatUSCurrency(voucherTotals.totalInvoicePaid)}
                                                    <div className="text-muted small">Cumulative</div>
                                                </td>
                                                <td className="text-end">
                                                    {formatUSCurrency(bankTxn.transaction_invoice_items.reduce((sum, item) =>
                                                        sum + parseFloat(item.paid_amount || 0), 0))}
                                                    <div className="text-muted small">Current</div>
                                                </td>
                                                <td className="text-end text-warning">{formatUSCurrency(voucherTotals.totalInvoiceBalance)}</td>
                                                <td className="text-center">
                                                    {voucherTotals.totalInvoiceAmount > 0 ?
                                                        ((voucherTotals.totalInvoicePaid / voucherTotals.totalInvoiceAmount) * 100).toFixed(1) : 0}%
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {voucherTotals.invoiceDetails.map((invoice, idx) => (
                                    invoice.items.length > 1 && (
                                        <div key={`details-${idx}`} className="border rounded p-3 mb-3">
                                            <h6 className="mb-2">Payment History for {invoice.invoice_ref}</h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Transaction</th>
                                                            <th className="text-end">Amount Paid</th>
                                                            <th className="text-end">Cumulative</th>
                                                            <th className="text-end">Balance</th>
                                                            <th className="text-center">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invoice.items.map((item, itemIdx) => (
                                                            <tr key={itemIdx}>
                                                                <td>Payment #{itemIdx + 1}</td>
                                                                <td className="text-end">{formatUSCurrency(item.paid_amount)}</td>
                                                                <td className="text-end text-success">
                                                                    {formatUSCurrency(
                                                                        invoice.items.slice(0, itemIdx + 1).reduce((sum, i) =>
                                                                            sum + parseFloat(i.paid_amount || 0), 0)
                                                                    )}
                                                                </td>
                                                                <td className="text-end text-warning">
                                                                    {formatUSCurrency(invoice.total_amount -
                                                                        invoice.items.slice(0, itemIdx + 1).reduce((sum, i) =>
                                                                            sum + parseFloat(i.paid_amount || 0), 0)
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className={`badge ${item.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                                        {item.is_fully_paid ? 'Fully Paid' : 'Partial'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        )} */}

                        {/* Invoices Section */}
                        {(bankTxn?.transaction_invoice_items?.length > 0 || voucherTotals?.invoiceDetails?.length > 0) && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fs-6">Invoices</h6>
                                    <span className="badge bg-secondary">
                                        {voucherTotals?.invoiceDetails?.length || 0} invoice(s)
                                    </span>
                                </div>

                                {/* Add a null check before mapping */}
                                {voucherTotals?.invoiceDetails && voucherTotals.invoiceDetails.length > 0 ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-sales-gradient">
                                                    <tr>
                                                        <th width="25%">Invoice No</th>
                                                        <th width="15%" className="text-end">Total Amount</th>
                                                        <th width="15%" className="text-end"> Paid</th>
                                                        <th width="15%" className="text-end">Current Payment</th>
                                                        <th width="15%" className="text-end">Balance</th>
                                                        <th width="15%" className="text-center">% Paid</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {voucherTotals.invoiceDetails.map((invoice, idx) => (
                                                        <tr key={idx}>
                                                            <td>
                                                                {invoice.invoice_ref}
                                                                <div className="text-muted small">
                                                                    Invoice ID: {invoice.invoice_id}
                                                                </div>
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {formatUSCurrency(invoice.total_amount)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {formatUSCurrency(invoice.cumulative_paid)}
                                                                {invoice.items?.length > 1 && (
                                                                    <div className="text-muted small">
                                                                        ({invoice.items.length} payments)
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="text-end">
                                                                {formatUSCurrency(invoice.items?.[invoice.items?.length - 1]?.paid_amount || 0)}

                                                            </td>
                                                            <td className="text-end text-warning">
                                                                {formatUSCurrency(invoice.cumulative_balance)}

                                                            </td>
                                                            <td className="text-center fw-bold">
                                                                <span className={`badge ${invoice.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                                    {invoice.total_amount > 0 ?
                                                                        ((invoice.cumulative_paid / invoice.total_amount) * 100).toFixed(1) : 0}%
                                                                </span>

                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {/* Invoice Totals Row - Show BOTH cumulative and current */}
                                                    <tr className=" fw-bold">
                                                        <td>Invoice Totals</td>
                                                        <td className="text-end">{formatUSCurrency(voucherTotals.totalInvoiceAmount)}</td>
                                                        <td className="text-end text-success">
                                                            {formatUSCurrency(voucherTotals.totalInvoicePaid)}
                                                        </td>
                                                        <td className="text-end">
                                                            {formatUSCurrency(bankTxn?.transaction_invoice_items?.reduce((sum, item) =>
                                                                sum + parseFloat(item.paid_amount || 0), 0) || 0)}
                                                        </td>
                                                        <td className="text-end text-warning">{formatUSCurrency(voucherTotals.totalInvoiceBalance)}</td>
                                                        <td className="text-center">
                                                            {voucherTotals.totalInvoiceAmount > 0 ?
                                                                ((voucherTotals.totalInvoicePaid / voucherTotals.totalInvoiceAmount) * 100).toFixed(1) : 0}%
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Show payment breakdown for each invoice */}
                                        {voucherTotals.invoiceDetails.map((invoice, idx) => (
                                            invoice.items?.length > 1 && (
                                                <div key={`details-${idx}`} className="border rounded p-3 mb-3">
                                                    <h6 className="mb-2">Payment History for {invoice.invoice_ref}</h6>
                                                    <div className="table-responsive">
                                                        <table className="table table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Transaction</th>
                                                                    <th className="text-end">Amount Paid</th>
                                                                    <th className="text-end">Cumulative</th>
                                                                    <th className="text-end">Balance</th>
                                                                    <th className="text-center">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {invoice.items.map((item, itemIdx) => (
                                                                    <tr key={itemIdx}>
                                                                        <td>Payment #{itemIdx + 1}</td>
                                                                        <td className="text-end">{formatUSCurrency(item.paid_amount)}</td>
                                                                        <td className="text-end text-success">
                                                                            {formatUSCurrency(
                                                                                invoice.items.slice(0, itemIdx + 1).reduce((sum, i) =>
                                                                                    sum + parseFloat(i.paid_amount || 0), 0)
                                                                            )}
                                                                        </td>
                                                                        <td className="text-end text-warning">
                                                                            {formatUSCurrency(invoice.total_amount -
                                                                                invoice.items.slice(0, itemIdx + 1).reduce((sum, i) =>
                                                                                    sum + parseFloat(i.paid_amount || 0), 0)
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <span className={`badge ${item.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                                                {item.is_fully_paid ? 'Fully Paid' : 'Partial'}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )
                                        ))}
                                    </>
                                ) : (
                                    <Alert variant="info">No invoice details available</Alert>
                                )}
                            </div>
                        )}

                        {/* Expenses Section with PO Summary */}
                        {bankTxn?.transaction_expense_items?.length > 0 && !isPayroll && (
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fs-6">Expenses & Purchase Orders</h6>
                                    <span className="badge bg-secondary">
                                        {bankTxn.transaction_expense_items.length} expense(s), {voucherTotals.poDetails.length} PO(s)
                                    </span>
                                </div>

                                {/* PO Summary Table */}
                                <div className="table-responsive mb-3">
                                    <table className="table table-sm table-bordered">
                                        <thead>
                                            <tr>
                                                <th colspan="6" className="text-center  text-dark">
                                                    PO Summary
                                                </th>
                                            </tr>
                                            <tr>
                                                <th width="20%">PO Reference</th>
                                                <th width="20%">Vendor</th>
                                                <th width="15%" className="text-end">PO Total</th>
                                                <th width="15%" className="text-end">Paid in Voucher</th>
                                                <th width="15%" className="text-end">PO Balance</th>
                                                <th width="15%" className="text-center">% Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* {voucherTotals.poDetails.map((po, poIndex) => (
                                                <tr key={poIndex}>
                                                    <td>
                                                        <div className="fw-medium">{po.po_reference_no}</div>
                                                    </td>
                                                    <td>{po.po_object?.vendor?.name || ''}</td>
                                                    <td className="text-end fw-bold">{formatUSCurrency(po.po_total_amount)}</td>
                                                    <td className="text-end text-success">{formatUSCurrency(po.total_paid)}</td>
                                                    <td className="text-end text-warning">{formatUSCurrency(getPOBalance(po.po_id))}</td>
                                                    <td className="text-center fw-bold">
                                                        {po.po_total_amount > 0 ?
                                                            ((po.total_paid / po.po_total_amount) * 100).toFixed(1) : 0}%
                                                    </td>
                                                </tr>
                                            ))} */}
                                            {voucherTotals.poDetails.map((po, poIndex) => {
                                                // Get PO total in PO's original currency
                                                const poTotal = po.po_total_amount;
                                                const poCurrency = po.po_currency || 'USD';

                                                // Get total paid in expense voucher currency (AED)
                                                const totalPaidInExpenseCurrency = po.expenses.reduce((sum, exp) => {
                                                    return sum + parseFloat(exp.paid_amount || 0);
                                                }, 0);
                                                const expenseCurrency = po.expenses[0]?.expense_currency || 'AED';

                                                // Convert paid amount to PO currency for calculations
                                                let totalPaidInPOCurrency = totalPaidInExpenseCurrency;
                                                let totalPaidInExpenseCurrencyConverted = totalPaidInExpenseCurrency;

                                                if (expenseCurrency === 'AED' && poCurrency === 'USD') {
                                                    totalPaidInPOCurrency = totalPaidInExpenseCurrency / 3.692; // AED to USD
                                                } else if (expenseCurrency === 'USD' && poCurrency === 'AED') {
                                                    totalPaidInPOCurrency = totalPaidInExpenseCurrency * 3.692; // USD to AED
                                                }

                                                // Calculate balance in PO currency
                                                const balanceInPOCurrency = poTotal - totalPaidInPOCurrency;

                                                // Calculate balance in expense currency (for brackets display)
                                                let balanceInExpenseCurrency = balanceInPOCurrency;
                                                if (poCurrency === 'USD' && expenseCurrency === 'AED') {
                                                    balanceInExpenseCurrency = balanceInPOCurrency * 3.692; // USD to AED
                                                } else if (poCurrency === 'AED' && expenseCurrency === 'USD') {
                                                    balanceInExpenseCurrency = balanceInPOCurrency / 3.692; // AED to USD
                                                }

                                                // Calculate paid percentage in PO currency
                                                const paidPercentage = poTotal > 0 ? (totalPaidInPOCurrency / poTotal) * 100 : 0;

                                                return (
                                                    <tr key={poIndex}>
                                                        <td>
                                                            <div className="fw-medium">{po.po_reference_no}</div>
                                                        </td>
                                                        <td>{po.po_object?.vendor?.name || ''}</td>

                                                        {/* PO Total - Always in PO currency */}
                                                        <td className="text-end">
                                                            {formatUSCurrency(poTotal)}
                                                            <span className='text-mute font-size-6'>({poCurrency})</span>
                                                        </td>

                                                        {/* Paid in Voucher - Show in expense currency first, PO currency in brackets */}
                                                        <td className="text-end text-success">
                                                            <div>
                                                                {formatUSCurrency(totalPaidInPOCurrency)} <span className='text-mute font-size-6'>({poCurrency})</span>
                                                            </div>
                                                            {/* {poCurrency !== expenseCurrency && (
                    <div className="text-muted small font-size-9">
                    ({formatUSCurrency(totalPaidInExpenseCurrency)} {expenseCurrency})

                    </div>
                )} */}
                                                        </td>

                                                        {/* Balance - Show in PO currency first, expense currency in brackets */}
                                                        <td className="text-end text-warning">
                                                            <div>
                                                                {formatUSCurrency(balanceInPOCurrency)}
                                                            </div>
                                                            {/* {poCurrency !== expenseCurrency && (
                    <div className="text-muted small">
                        ≈ {formatUSCurrency(balanceInExpenseCurrency)} {expenseCurrency}
                    </div>
                )} */}
                                                        </td>

                                                        {/* Paid Percentage - Always calculated in PO currency */}
                                                        <td className="text-center fw-bold">
                                                            {paidPercentage.toFixed(1)}%
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {/* Expense Totals Row */}
                                            <tr className="table-secondary fw-bold">
                                                <td colSpan="2">Expense Totals</td>
                                                <td className="text-end">{formatUSCurrency(voucherTotals.totalExpenseAmount)}</td>
                                                <td className="text-end text-success">{formatUSCurrency(voucherTotals.totalExpensePaid)}</td>
                                                <td className="text-end text-warning">{formatUSCurrency(voucherTotals.totalExpenseBalance)}</td>
                                                <td className="text-center">
                                                    {voucherTotals.totalExpenseAmount > 0 ?
                                                        ((voucherTotals.totalExpensePaid / voucherTotals.totalExpenseAmount) * 100).toFixed(1) : 0}%
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Expense Details by PO */}
                                {voucherTotals.poDetails.map((po, poIndex) => (
                                    <div key={poIndex} className="mb-3">
                                        <div className="bg-light p-2 mb-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong>PO: {po.po_reference_no}</strong>
                                                    <div className="text-muted small">
                                                        Vendor: {po.po_object?.vendor?.name || 'N/A'} |
                                                        PO Total: {formatUSCurrency(po.po_total_amount)} |
                                                        Balance: {formatUSCurrency(getPOBalance(po.po_id))}
                                                    </div>
                                                </div>
                                                <span className="badge bg-info">
                                                    {po.expenses.length} expense(s)
                                                </span>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover mb-3">
                                                <thead className="table-sales-gradient">
                                                    <tr>
                                                        <th width="30%">Expense No</th>
                                                        <th width="30%">Description</th>
                                                        <th width="15%" className="text-end">Amount</th>
                                                        <th width="15%" className="text-end">Paid</th>
                                                        <th width="10%" className="text-center">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {po.expenses.map((expense, expIndex) => (
                                                        <tr key={expIndex}>
                                                            <td>{expense.expense_no}</td>
                                                            <td>{expense.expense_object?.narration || 'N/A'}</td>
                                                            <td className="text-end">{formatUSCurrency(expense.amount)}</td>
                                                            <td className="text-end text-success">{formatUSCurrency(expense.paid_amount)}</td>
                                                            <td className="text-center">
                                                                <span className={`badge ${expense.is_fully_paid ? 'bg-success' : 'bg-warning'}`}>
                                                                    {expense.paid_percentage.toFixed(1)}%
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isAdvancePayment && bankTxn.entryDetails && !isCapitalTransaction && !isPayroll && (
                            <Card className="mb-4">
                                <Card.Body>
                                    <h6 className="mb-3">Advance Payment Details</h6>
                                    <div className="advance-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Ledger Entry ID:</span>
                                            <span>{bankTxn.entryDetails.id}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Voucher Type:</span>
                                            <span>{bankTxn.entryDetails.entrytype || 'Payment Voucher'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Reference No:</span>
                                            <span>{bankTxn.entryDetails.reference_no || 'N/A'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Narration:</span>
                                            <span>{bankTxn.entryDetails.narration || 'N/A'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Date:</span>
                                            <span>{formatDateForDisplay(bankTxn.entryDetails.date)}</span>
                                        </div>
                                    </div>


                                </Card.Body>
                            </Card>
                        )}
                        {isCapitalTransaction && bankTxn.entryDetails && (
                            <Card className="mb-4">
                                <Card.Body>
                                    <h6 className="mb-3">Capital Transaction Details</h6>
                                    <div className="advance-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Ledger Entry ID:</span>
                                            <span>{bankTxn.entryDetails.id}</span>
                                        </div>


                                        <div className="detail-row">
                                            <span className="detail-label">Reference No:</span>
                                            <span>{bankTxn.entryDetails.reference_no || 'N/A'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Narration:</span>
                                            <span>{bankTxn.entryDetails.narration || 'N/A'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Date:</span>
                                            <span>{formatDateForDisplay(bankTxn.entryDetails.date)}</span>
                                        </div>
                                    </div>


                                </Card.Body>
                            </Card>
                        )}
                        {/* Financial Summary Card */}
                        {!isPayroll && (
                            <div className="card border-0 shadow-sm mb-4 bg-light">
                                <div className="card-body p-3">
                                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3">Financial Summary</h6>
                                    <div className="row g-3">
                                        <div className="col-md-6 border-end">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small">Total Voucher Amount:</span>
                                                <span className="fw-bold">{formatUSCurrency(voucherTotals.totalAmount)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small">Total Paid:</span>
                                                <span className="fw-bold text-success">{formatUSCurrency(voucherTotals.totalPaid)}</span>
                                            </div>
                                            {/* <div className="d-flex justify-content-between pt-2 border-top">
                                                <span className="text-muted small">Remaining Balance:</span>
                                                <span className="fw-bold text-danger">{formatUSCurrency(voucherTotals.totalBalance)}</span>
                                            </div> */}
                                        </div>
                                        <div className="col-md-6">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small">Base Bank Amount:</span>
                                                <span className="fw-bold">{formatUSCurrency(parseFloat(bankTxn?.amount || 0) - (parseFloat(bankTxn?.deduction_amount || 0)))}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small">Bank Charges:</span>
                                                <span className="fw-bold text-black">{formatUSCurrency(parseFloat(bankTxn?.deduction_amount || 0) || parseFloat(bankTxn?.bank_service_charge || 0) || parseFloat(bankTxn?.wps_charge || 0))}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted small">Bank VAT Amount:</span>
                                                <span className="fw-bold text-black">{formatUSCurrency(parseFloat(bankTxn?.bank_vat_amount || 0) || parseFloat(bankTxn?.wps_vat || 0))}</span>
                                            </div>
                                            {/* <div className="d-flex justify-content-between pt-2 border-top">
                                                <span className="text-muted small">Total Bank Transaction:</span>
                                                <span className="fw-bold text-primary">{formatUSCurrency(bankTxn?.amount || 0)} {bankTxn?.currency}</span>
                                            </div> */}
                                        </div>
                                    </div>
                                    {/* {voucherTotals.totalAmount > 0 && (
                                        <div className="mt-3 pt-2 border-top">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className="text-muted small">Payment Completion Strategy:</span>
                                                <div className="progress flex-grow-1 mx-3" style={{ height: '8px' }}>
                                                    <div
                                                        className={`progress-bar ${voucherTotals.totalPaid >= voucherTotals.totalAmount ? 'bg-success' : 'bg-primary'}`}
                                                        style={{ width: `${Math.min(100, (voucherTotals.totalPaid / voucherTotals.totalAmount) * 100)}%` }}
                                                    ></div>
                                                </div>
                                                <span className="fw-bold small">{((voucherTotals.totalPaid / voucherTotals.totalAmount) * 100).toFixed(1)}%</span>
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            </div>
                        )}
                        {isPayroll && (
                            <div className="mb-4">
                                <h6 className="mb-3 fw-bold border-bottom pb-2">Payroll Details</h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td width="50%" className="text-muted">Net Payroll Amount:</td>
                                                    <td className="fw-bold">{formatUSCurrency(bankTxn?.net_payroll_amount || 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">WPS Charge:</td>
                                                    <td className="text-danger">{formatUSCurrency(bankTxn?.wps_charge || 0)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td width="50%" className="text-muted">VAT on WPS:</td>
                                                    <td className="text-danger">{formatUSCurrency(bankTxn?.wps_vat || 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">Total Bank Amount:</td>
                                                    <td className="fw-bold text-primary">
                                                        {formatUSCurrency(bankTxn?.bank_amount || bankTxn?.amount || 0)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Payroll Calculation Breakdown */}
                                <Card className="mt-3">
                                    <Card.Body className="p-3">
                                        <h6 className="mb-2">Calculation Breakdown</h6>
                                        <div className="calculation-steps">
                                            <div className="step">
                                                <span className="step-label">Net Payroll:</span>
                                                <span className="step-value">{formatUSCurrency(bankTxn?.net_payroll_amount || 0)}</span>
                                            </div>
                                            <div className="step text-danger">
                                                <span className="step-label">+ WPS Charge:</span>
                                                <span className="step-value">+ {formatUSCurrency(bankTxn?.wps_charge || 0)}</span>
                                            </div>
                                            <div className="step text-danger">
                                                <span className="step-label">+ VAT on WPS:</span>
                                                <span className="step-value">+ {formatUSCurrency(bankTxn?.wps_vat || 0)}</span>
                                            </div>
                                            <hr className="my-2" />
                                            <div className="step fw-bold">
                                                <span className="step-label">Total Bank Debit:</span>
                                                <span className="step-value text-primary">
                                                    {formatUSCurrency(bankTxn?.bank_amount || bankTxn?.amount || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        )}
                    </div>


                    {/* Section 3: Bank Reconciliation */}
                    {/* <div className="mb-4">
                        <h6 className="mb-3 fw-bold border-bottom pb-2">Bank Reconciliation</h6>

                        <div className="row align-items-center mb-4">
                            <div className="col-5">
                                <div className="border p-3 rounded">
                                    <small className="text-muted d-block">Expected Bank Amount</small>
                                    <div className="fs-3 fw-bold">{formatUSCurrency(voucherTotals.expectedBankAmount)}</div>
                                    {isReceipt && deductionAmount > 0 && (
                                        <div className="text-danger small mt-1">
                                            Less deduction: {formatUSCurrency(deductionAmount)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-2 text-center">
                                <div className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                    style={{ width: '40px', height: '40px' }}>
                                    →
                                </div>
                            </div>
                            <div className="col-5">
                                <div className="border p-3 rounded border-primary">
                                    <small className="text-muted d-block">Actual Bank Amount</small>
                                    <div className="fs-3 fw-bold text-primary">{formatUSCurrency(voucherTotals.bankAmount)}</div>
                                </div>
                            </div>
                        </div>

                        <div className={`border rounded p-3 mb-3 ${voucherTotals.isExactMatch ? 'border-success bg-success bg-opacity-10' : 'border-danger bg-danger bg-opacity-10'}`}>
                            <div className="text-center">
                                <small className="text-muted d-block mb-1">Reconciliation Difference</small>
                                <div className={`fs-2 fw-bold ${voucherTotals.isExactMatch ? 'text-success' : 'text-danger'}`}>
                                    {voucherTotals.difference > 0 ? '+' : ''}{formatUSCurrency(voucherTotals.difference)}
                                    <span className="fs-5 ms-2">
                                        ({voucherTotals.differencePercentage}%)
                                    </span>
                                </div>
                                <div className={`mt-1 ${voucherTotals.isExactMatch ? 'text-success' : 'text-danger'}`}>
                                    {voucherTotals.isExactMatch ? '✓ Amounts match perfectly' : '✗ Amount mismatch detected'}
                                </div>
                            </div>
                        </div>

                        {!voucherTotals.isExactMatch && Math.abs(voucherTotals.difference) > 0.01 && (
                            <div className="mt-3">
                                <label className="form-label fw-medium">Reconciliation Notes</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    value={differenceReason}
                                    onChange={(e) => setDifferenceReason(e.target.value)}
                                    placeholder="Explain the difference between expected and actual bank amounts..."
                                    style={{ resize: 'none' }}
                                />
                                <div className="form-text small">
                                    This note will be recorded in the reconciliation audit trail.
                                </div>
                            </div>
                        )}
                    </div> */}
                    <div className="mb-4">
                        <h6 className="mb-3 fw-bold border-bottom pb-2">Bank Reconciliation</h6>

                        {isAdvancePayment || isPayroll || isContra || isVatPayable ? (
                            /* Simplified view for specialized payments */
                            <div className="p-4 border rounded bg-soft-primary bg-opacity-10 border-primary border-opacity-25">
                                <div className="text-center mb-3">
                                    <div className="font-size-11 text-uppercase fw-bold text-muted mb-1">
                                        Total {isCapitalTransaction ? 'Capital' : isAdvancePayment ? 'Advance' : isContra ? 'Contra' : isVatPayable ? 'VAT' : 'Payroll'} Amount
                                    </div>
                                    <h3 className="fw-bold text-primary mb-0">
                                        {formatUSCurrency(voucherTotals.bankAmount)} <small className="fs-6 fw-normal">{bankTxn?.currency || 'AED'}</small>
                                    </h3>
                                </div>

                                <Row className="gy-3 mt-1 border-top pt-3">
                                    <Col md={6}>
                                        <label className="text-muted small d-block mb-1">Transaction Category</label>
                                        <div className="fw-bold text-dark">{transactionType}</div>
                                    </Col>
                                    <Col md={6}>
                                        <label className="text-muted small d-block mb-1">Reference No.</label>
                                        <div className="fw-medium text-dark">{bankTxn?.reference || 'N/A'}</div>
                                    </Col>
                                    {(bankTxn?.vendor?.name || bankTxn?.client?.name) && (
                                        <Col md={12}>
                                            <label className="text-muted small d-block mb-1">
                                                {isCapitalTransaction ? 'Partner / Contributor' : isAdvancePayment ? (bankTxn.advance_type === 'vendor' ? 'Vendor' : 'Client') : 'Related Entity'}
                                            </label>
                                            <div className="fw-bold text-primary">{bankTxn?.vendor?.name || bankTxn?.client?.name}</div>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        ) : (
                            /* Regular reconciliation view */
                            <>
                                <div className="row align-items-center mb-4">
                                    <div className="col-5">
                                        <div className="border p-3 rounded">
                                            <small className="text-muted d-block">Expected Bank Amount</small>
                                            <div className="fs-3 fw-bold">{formatUSCurrency(voucherTotals.expectedBankAmount)}</div>
                                            {deductionAmount > 0 && (
                                                <div className="text-danger small mt-1">
                                                    {isReceipt ? 'Less deduction' : 'Plus deduction'}: {formatUSCurrency(deductionAmount)}
                                                    {(parseFloat(bankTxn?.bank_service_charge || 0) > 0 || parseFloat(bankTxn?.bank_vat_amount || 0) > 0) && (
                                                        <div className="smallest opacity-75" style={{ fontSize: '10px' }}>
                                                            (Charge: {formatUSCurrency(bankTxn.bank_service_charge)}, VAT: {formatUSCurrency(bankTxn.bank_vat_amount)})
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-2 text-center">
                                        <div className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                            style={{ width: '40px', height: '40px' }}>
                                            →
                                        </div>
                                    </div>
                                    <div className="col-5">
                                        <div className="border p-3 rounded border-primary">
                                            <small className="text-muted d-block">Actual Bank Amount</small>
                                            <div className="fs-3 fw-bold text-primary">{formatUSCurrency(voucherTotals.bankAmount)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Difference Display - Only show for regular transactions if not exact match */}
                                {!voucherTotals.isExactMatch && Math.abs(voucherTotals.difference) > 0.01 && (
                                    <div className={`border rounded p-3 mb-3 border-danger bg-danger bg-opacity-10`}>
                                        <div className="text-center">
                                            <small className="text-muted d-block mb-1">Reconciliation Difference</small>
                                            <div className="fs-2 fw-bold text-danger">
                                                {voucherTotals.difference > 0 ? '+' : ''}{formatUSCurrency(voucherTotals.difference)}
                                                <span className="fs-5 ms-2">
                                                    ({voucherTotals.differencePercentage}%)
                                                </span>
                                            </div>
                                            <div className="mt-1 text-danger">
                                                ✗ Amount mismatch detected
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between pt-3 border-top">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={onHide}
                        >
                            Cancel
                        </button>
                        <button
                            className={`btn btn-primary ${loadingReconcile ? 'disabled' : ''}`}
                            onClick={handleReconcile}
                            disabled={loadingReconcile}
                            style={{ minWidth: '200px' }}
                        >
                            {loadingReconcile ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Processing...
                                </>
                            ) : isAdvancePayment ? (
                                'Reconcile Advance Payment'
                            ) : isCapitalTransaction ? (
                                'Reconcile Capital Transaction'
                            ) : isBankGuarantee ? (
                                'Reconcile Bank Guarantee'
                            ) : isBankGuaranteeReleased ? (
                                'Reconcile BG Released'
                            ) : isBankGuaranteeCharges ? (
                                'Reconcile BG Charges'
                            ) : isBadDebtWrittenOff ? (
                                'Reconcile Bad Debt'
                            ) : isContra ? (
                                'Reconcile Contra'
                            ) : isVatPayable ? (
                                'Reconcile VAT Payment'
                            ) : isPayroll ? (
                                'Reconcile Payroll'
                            ) : (
                                'Confirm Reconciliation'
                            )}
                        </button>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        );

    };
    const ReconciliationDetailsModal = ({ show, onHide, transaction }) => {
        const [journalEntry, setJournalEntry] = useState(null);
        const [loading, setLoading] = useState(false);
        const lastFetchedRef = useRef({});

        useEffect(() => {
            if (!show || !transaction) return;

            const journalId = transaction?.journal_id;

            if (journalId) {
                if (lastFetchedRef.current === journalId) return;
                lastFetchedRef.current = journalId;

                let cancelled = false;

                const fetchJournal = async () => {
                    setLoading(true);
                    try {
                        const res = await axios.get(`${Api}/api/entries/getEntryById`, {
                            headers: { "x-api-key": apiKey },
                            params: { entry_id: journalId },
                            skipLoader: true,
                        });

                        if (!cancelled) {
                            const entry = res.data?.entry || null;
                            const related = res.data?.related_entry || null;
                            setJournalEntry(entry ? { ...entry, related_entry: related } : null);
                        }
                    } catch (err) {
                        if (!cancelled) {
                            setJournalEntry(null);
                            console.error("Journal fetch error", err);
                        }
                    } finally {
                        if (!cancelled) setLoading(false);
                    }
                };

                fetchJournal();
            } else {
                setJournalEntry(null);
                setLoading(false);
            }

            return () => {
                let cancelled = true;
            };
        }, [show, transaction?.journal_id]);

        useEffect(() => {
            if (!show) {
                lastFetchedRef.current = null;
                setJournalEntry(null);
            }
        }, [show]);

        const formatDate = (dateString) => {
            return formatDateForDisplay(dateString);
        };

        const isAdvance = transaction?.type === 'advance' || transaction?._rawTransaction?.type === 'advance';
        const isCapital = transaction?.type === 'capital' || transaction?._rawTransaction?.type === 'capital' || transaction?.is_capital;
        const isContra = transaction?.type === 'contra' || transaction?._rawTransaction?.type === 'contra';
        const isWithdrawal = transaction?.type === 'withdrawal' || transaction?._rawTransaction?.type === 'withdrawal' || transaction?.is_withdrawal;
        const isVatPayable = transaction?.type === 'vat_payable' || transaction?._rawTransaction?.type === 'vat_payable';
        const isPayroll = transaction?.type === 'payroll' || transaction?._rawTransaction?.type === 'payroll' || transaction?.is_payroll;
        const isBankGuarantee = transaction?.type === 'bank_guarantee' || transaction?._rawTransaction?.type === 'bank_guarantee' || transaction?.is_bank_guarantee;
        const isBankGuaranteeReleased = transaction?.type === 'bank_guarantee_released' || transaction?._rawTransaction?.type === 'bank_guarantee_released' || transaction?.is_bank_guarantee_released;
        const isBankGuaranteeCharges = transaction?.type === 'bank_guarantee_charges' || transaction?._rawTransaction?.type === 'bank_guarantee_charges' || transaction?.is_bank_guarantee_charges;
        const isBadDebtWrittenOff = transaction?.type === 'bad_debt_written_off' || transaction?._rawTransaction?.type === 'bad_debt_written_off' || transaction?.is_bad_debt_written_off;
        const isReceipt = transaction?.entrytype_id === 1 || transaction?.type === 'receipt';
        const isPayment = transaction?.entrytype_id === 2 || transaction?.type === 'payment';

        const displayType = isAdvance ? (transaction?.advance_type === 'client' ? 'Client Advance' : transaction?.advance_type === 'vendor' ? 'Vendor Advance' : (isReceipt ? 'Customer Advance' : 'Supplier Advance')) :
            isCapital ? 'Capital' :
                isContra ? 'Contra' :
                    isPayroll ? 'Payroll' :
                        isWithdrawal ? 'Withdrawal' :
                            isVatPayable ? 'VAT Payment' :
                                isBankGuarantee ? 'Bank Guarantee' :
                                    isBankGuaranteeReleased ? 'BG Released' :
                                        isBankGuaranteeCharges ? 'BG Charges' :
                                            isBadDebtWrittenOff ? 'Bad Debt Written Off' :
                                                isPayment ? 'Expense' :
                                                    isReceipt ? 'Invoice' : 'Other';

        const calculateVatDetails = () => {
            let baseAmount = 0;
            let vatAmount = 0;
            let totalAmount = 0;

            if (isReceipt && transaction?.invoice) {
                baseAmount = parseFloat(transaction.invoice.basic_amount || 0);
                vatAmount = parseFloat(transaction.invoice.vat || 0);
                totalAmount = parseFloat(transaction.invoice.amount || 0);
            } else if (isPayment && transaction?.expense) {
                baseAmount = parseFloat(transaction.expense.amount_without_tax || 0);
                vatAmount = parseFloat(transaction.expense.tax_amount || 0) ||
                    (parseFloat(transaction.expense.tax || 0) > 0 ? (baseAmount * parseFloat(transaction.expense.tax) / 100) : 0);
                totalAmount = parseFloat(transaction.expense.total_amount || 0);
            } else {
                const bankTotal = parseFloat(transaction?.amount || 0);
                
                let deduction = parseFloat(transaction?.deduction_amount || 0);
                if (deduction === 0) {
                    if (isPayroll) {
                        deduction = parseFloat(transaction?.wps_charge || 0) + parseFloat(transaction?.wps_vat || 0);
                    } else if (isBankGuarantee || isBankGuaranteeCharges) {
                        deduction = parseFloat(transaction?.commission_fee || 0) + parseFloat(transaction?.commission_vat || 0) + 
                                   parseFloat(transaction?.bank_service_charge || 0) + parseFloat(transaction?.bank_vat_amount || 0);
                    } else {
                        deduction = parseFloat(transaction?.bank_service_charge || 0) + parseFloat(transaction?.bank_vat_amount || 0);
                    }
                }
                
                if (isReceipt) {
                    // For receipts, bank charges are usually deducted from what we receive
                    totalAmount = bankTotal + deduction;
                } else {
                    // For payments, bank charges are added to our original payment amount
                    totalAmount = bankTotal - deduction;
                }
                
                baseAmount = totalAmount;
            }

            return {
                baseAmount,
                vatAmount,
                totalAmount,
                vatPercentage: baseAmount > 0 ? ((vatAmount / baseAmount) * 100).toFixed(1) : '0.0',
                hasVat: vatAmount > 0
            };
        };

        const vatDetails = calculateVatDetails();
        
        // Robust deduction calculation to avoid double counting
        let calculatedDeduction = parseFloat(transaction?.deduction_amount || 0);
        if (calculatedDeduction === 0) {
            if (isPayroll) {
                calculatedDeduction = parseFloat(transaction?.wps_charge || 0) + parseFloat(transaction?.wps_vat || 0);
            } else if (isBankGuarantee || isBankGuaranteeCharges) {
                calculatedDeduction = parseFloat(transaction?.commission_fee || 0) + parseFloat(transaction?.commission_vat || 0) + 
                                     parseFloat(transaction?.bank_service_charge || 0) + parseFloat(transaction?.bank_vat_amount || 0);
            } else {
                calculatedDeduction = parseFloat(transaction?.bank_service_charge || 0) + parseFloat(transaction?.bank_vat_amount || 0);
            }
        }
        
        const deductionAmount = calculatedDeduction;
        const bankAmount = parseFloat(transaction?.amount || 0);
        const expectedBankAmount = isReceipt ? (vatDetails.totalAmount - deductionAmount) : (vatDetails.totalAmount + deductionAmount);

        return (
            <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '800px' }}>
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">
                        {displayType} Details
                        <Badge bg="info" className="ms-2 font-size-12 fw-normal">#{transaction?.id}</Badge>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="bg-light">
                    {loading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading complete transaction history...</p>
                        </div>
                    ) : (
                        <div className="transaction-detail-view px-2">
                            {/* Status Banner */}
                            <div className={`alert ${transaction?.reconciliation_status === 'reconciled' ? 'alert-success' : transaction?.reconciliation_status === 'reversed' ? 'alert-danger' : 'alert-warning'} d-flex align-items-center mb-4 border-0 shadow-sm rounded-3`}>
                                {transaction?.reconciliation_status === 'reconciled' ? <FiCheckCircle className="me-2 fs-4" /> : <FiAlertCircle className="me-2 fs-4" />}
                                <div className="flex-grow-1">
                                    <h6 className="mb-0 fw-bold">
                                        {transaction?.reconciliation_status === 'reconciled' ? 'Reconciled Successfully' : transaction?.reconciliation_status === 'reversed' ? 'Reconciliation Reversed' : 'Pending Reconciliation'}
                                    </h6>
                                    {transaction?.reconciled_at && (
                                        <small className="opacity-75">Processed on {formatDate(transaction.reconciled_at)}</small>
                                    )}
                                </div>
                            </div>

                            <Row>
                                <Col lg={12}>
                                    {/* Section 1: Bank Transaction Details */}
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                                <h6 className="text-primary text-uppercase font-size-12 fw-bold mb-0">
                                                    <FiCreditCard className="me-2" />
                                                    Bank Transaction Details
                                                </h6>
                                                <span className="badge bg-soft-primary text-primary font-size-11">#{transaction?.id}</span>
                                            </div>

                                            <Row className="gy-3">
                                                <Col md={6}>
                                                    <label className="text-muted small d-block mb-1">Transaction Date</label>
                                                    <div className="fw-medium text-dark">{formatDate(transaction?.date)}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <label className="text-muted small d-block mb-1">Bank Account</label>
                                                    <div className="fw-medium text-dark">{transaction?.accounts?.name || 'N/A'}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <label className="text-muted small d-block mb-1">Reference Number</label>
                                                    <div className="fw-bold text-dark">{transaction?.reference || 'N/A'}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <label className="text-muted small d-block mb-1">Bank Currency</label>
                                                    <div className="fw-medium text-dark">{transaction?.currency || 'AED'}</div>
                                                </Col>
                                                <Col md={6}>
                                                    <label className="text-muted small d-block mb-1">Bank Amount</label>
                                                    <div className="d-flex align-items-center">
                                                        {(() => {
                                                            const rawType = transaction?._rawTransaction?.type || transaction?.type;
                                                            const isContra = rawType === 'contra';
                                                            const isMoneyIn = (rawType === 'income' || (isContra && transaction?.isSelectedTo) || transaction?.entrytype_id === 1);
                                                            const isMoneyOut = (rawType === 'expense' || rawType === 'payroll' || rawType === 'vat_payable' || rawType === 'withdrawal' || (isContra && transaction?.isSelectedFrom) || transaction?.entrytype_id === 2);

                                                            if (isMoneyIn) return <FiArrowDownLeft className="text-success me-2 fw-bold fs-5" />;
                                                            if (isMoneyOut) return <FiArrowUpRight className="text-danger me-2 fw-bold fs-5" />;
                                                            return null;
                                                        })()}
                                                        <div className="fw-bold text-dark fs-5">
                                                            {formatUSCurrency(transaction?.amount || 0)}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={12}>
                                                    <label className="text-muted small d-block mb-1">Original Description</label>
                                                    <div className="text-muted p-2 bg-light rounded font-size-13">
                                                        {transaction?.description || 'No description provided'}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {/* Section 2: Type Specific Details */}
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                                <h6 className="text-warning text-uppercase font-size-12 fw-bold mb-0">
                                                    <FiFolder className="me-2" />
                                                    {displayType} Information
                                                </h6>
                                            </div>

                                            {isPayment && transaction?.expense ? (
                                                <Row className="gy-3">
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Expense Invoice No</label>
                                                        <div className="fw-bold text-dark">{transaction.expense.invoice_no || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Vendor</label>
                                                        <div className="fw-bold text-primary">{transaction?.vendor?.name || 'N/A'}</div>
                                                    </Col>
                                                    {transaction?.client?.name && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">Client (Billable)</label>
                                                            <div className="fw-medium text-dark">{transaction.client.name}</div>
                                                        </Col>
                                                    )}
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Expense Date</label>
                                                        <div className="fw-medium text-dark">{formatDate(transaction.expense.date)}</div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Expense Narration</label>
                                                        <div className="text-dark font-size-13">{transaction.expense.narration || 'N/A'}</div>
                                                    </Col>
                                                    {/* Projects - Handle Array/Object */}
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                    {transaction?._rawTransaction?.expense?.purchase_order && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">PO Reference</label>
                                                            <div className="badge bg-soft-secondary text-secondary p-2 px-3 rounded-pill fw-medium">
                                                                {transaction._rawTransaction.expense.purchase_order.ref_no || ''}
                                                            </div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            ) : isReceipt && transaction?.invoice ? (
                                                <Row className="gy-3">
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Invoice Reference No</label>
                                                        <div className="fw-bold text-dark">{transaction.invoice.ref_no || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Client</label>
                                                        <div className="fw-bold text-primary">{transaction?.client?.name || 'N/A'}</div>
                                                    </Col>

                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Invoice Date</label>
                                                        <div className="fw-medium text-dark">{formatDate(transaction.invoice.created_at)}</div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Description</label>
                                                        <div className="text-dark font-size-13">{transaction.invoice.description || 'N/A'}</div>
                                                    </Col>
                                                    {/* Projects - Handle Array/Object */}
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            ) : (isBankGuarantee || isBankGuaranteeCharges) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        <div className="alert alert-soft-info border-0 mb-0 d-flex align-items-center">
                                                            <FiInfo className="me-2" />
                                                            <span>Breakdown of <strong>{displayType}</strong> and associated treasury charges.</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Bank Name</label>
                                                        <div className="fw-bold text-dark">{transaction?.accounts?.name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Reference</label>
                                                        <div className="fw-medium text-dark">{transaction?.reference || 'N/A'}</div>
                                                    </Col>

                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Commission Charge</label>
                                                        <div className="fw-bold text-dark">{formatUSCurrency(transaction?.commission_fee || 0)}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Commission VAT</label>
                                                        <div className="fw-bold text-dark">{formatUSCurrency(transaction?.commission_vat || 0)}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Bank Charge</label>
                                                        <div className="fw-bold text-dark">{formatUSCurrency(transaction?.bank_service_charge || 0)}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Bank VAT</label>
                                                        <div className="fw-bold text-dark">{formatUSCurrency(transaction?.bank_vat_amount || 0)}</div>
                                                    </Col>

                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            ) : (isBankGuaranteeReleased) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        <div className="alert alert-soft-success border-0 mb-0 d-flex align-items-center">
                                                            <FiCheckCircle className="me-2" />
                                                            <span>Bank Guarantee has been <strong>Released/Cancelled</strong>.</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Guarantee Name</label>
                                                        <div className="fw-bold text-primary">{transaction?.guarantee_name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Guarantee ID</label>
                                                        <div className="badge bg-soft-info text-info p-2 px-3 rounded-pill fw-medium">
                                                            #{transaction?.guarantee_id || 'N/A'}
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Bank Name</label>
                                                        <div className="fw-bold text-dark">{transaction?.accounts?.name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                </Row>

                                            ) : (isBadDebtWrittenOff) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        <div className="alert alert-soft-danger border-0 mb-0 d-flex align-items-center">
                                                            <FiAlertOctagon className="me-2" />
                                                            <span>Accounting adjustment for uncollectible receivables.</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Client Name</label>
                                                        <div className="fw-bold text-danger">
                                                            {transaction?.client?.name || journalEntry?.client?.name || 'N/A'}
                                                        </div>
                                                    </Col>
                                                    {journalEntry?.ledger_entry && (
                                                        <Col md={12}>
                                                            <label className="text-muted small d-block mb-1">Adjustment Ledger</label>
                                                            <div className="fw-medium text-dark">{journalEntry?.ledger_entry?.ledger_name || 'N/A'}</div>
                                                        </Col>
                                                    )}
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            ) : (isAdvance) ? (
                                                <Row className="gy-3">
                                                    {(transaction?.client || journalEntry?.client) && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">Client</label>
                                                            <div className="fw-bold text-primary">{transaction?.client?.name || journalEntry?.client?.name || 'N/A'}</div>
                                                        </Col>
                                                    )}
                                                    {(transaction?.vendor || journalEntry?.vendor) && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">Vendor</label>
                                                            <div className="fw-bold text-primary">{transaction?.vendor?.name || journalEntry?.vendor?.name || 'N/A'}</div>
                                                        </Col>
                                                    )}
                                                    {transaction?.advance_type && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">Advance Type</label>
                                                            <div className="badge bg-soft-info text-info p-2 px-3 rounded-pill fw-medium text-capitalize">
                                                                {transaction.advance_type} Advance
                                                            </div>
                                                        </Col>
                                                    )}
                                                    {journalEntry?.ledger_entry && (
                                                        <Col md={12}>
                                                            <label className="text-muted small d-block mb-1">Ledger Entry</label>
                                                            <div className="fw-medium text-dark">{journalEntry?.ledger_entry?.ledger_name || 'N/A'}</div>
                                                        </Col>
                                                    )}
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project(s)</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {renderProjectBadges(transaction)}
                                                        </div>
                                                    </Col>
                                                    {(transaction?._rawTransaction?.purchase_order || transaction?._rawTransaction?.advance_details?.purchase_order) && (
                                                        <>
                                                            <Col md={6}>
                                                                <label className="text-muted small d-block mb-1">PO Reference</label>
                                                                <div className="badge bg-soft-secondary text-secondary p-2 px-3 rounded-pill fw-medium">
                                                                    {transaction._rawTransaction.purchase_order?.ref_no || transaction._rawTransaction.advance_details?.purchase_order?.ref_no || 'N/A'}
                                                                </div>
                                                            </Col>
                                                            <Col md={6}>
                                                                <label className="text-muted small d-block mb-1">PO Total Amount</label>
                                                                <div className="fw-bold text-dark">
                                                                    {formatUSCurrency(transaction._rawTransaction.purchase_order?.amount || transaction._rawTransaction.advance_details?.purchase_order?.total_amount || 0)}
                                                                </div>
                                                            </Col>
                                                        </>
                                                    )}
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Advance Amount</label>
                                                        <div className="fw-bold text-primary">{formatUSCurrency(vatDetails.totalAmount)}</div>
                                                    </Col>
                                                </Row>
                                            ) : (isCapital) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        <div className="alert alert-soft-success border-0 mb-0 d-flex align-items-center">
                                                            <FiCheckCircle className="me-2 text-success" />
                                                            <span>This is a <strong>Capital Contribution/Adjustment</strong> recorded in the books.</span>
                                                        </div>
                                                    </Col>

                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Capital Amount</label>
                                                        <div className="fw-bold text-dark fs-5">{formatUSCurrency(vatDetails.totalAmount)}</div>
                                                    </Col>

                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Transaction Ref</label>
                                                        <div className="badge bg-soft-info text-info p-2 px-3 rounded-pill fw-medium">
                                                            {transaction?.reference || ''}
                                                        </div>
                                                    </Col>

                                                </Row>
                                            ) : (isContra) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        {transaction.isSelectedTo ? (
                                                            <div className="alert alert-soft-success border-0 mb-0 d-flex align-items-center">
                                                                <FiArrowDownLeft className="fs-18 me-2" />
                                                                <span>This is a <strong>Funds Transfer Receipt</strong> (Debit) to the current account.</span>
                                                            </div>
                                                        ) : transaction.isSelectedFrom ? (
                                                            <div className="alert alert-soft-danger border-0 mb-0 d-flex align-items-center">
                                                                <FiArrowUpRight className="fs-18 me-2" />
                                                                <span>This is a <strong>Funds Transfer Payment</strong> (Credit) from the current account.</span>
                                                            </div>
                                                        ) : null}
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">From Account</label>
                                                        <div className="fw-bold text-danger">{transaction?.from_bank_name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">To Account</label>
                                                        <div className="fw-bold text-success">{transaction?.to_bank_name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project</label>
                                                        {renderProjectBadges(transaction)}
                                                    </Col>
                                                </Row>
                                            ) : (isWithdrawal) ? (
                                                <Row className="gy-3">
                                                    <Col md={12}>
                                                        <div className="alert alert-soft-danger border-0 mb-0 d-flex align-items-center">
                                                            <FiArrowUpRight className="fs-18 me-2" />
                                                            <span>This is a <strong>Cash Withdrawal</strong> (Credit) from the bank.</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Withdrawal Bank</label>
                                                        <div className="fw-bold text-danger">{transaction?.accounts?.name || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Bank Reference</label>
                                                        <div className="fw-bold text-dark">{transaction?.reference || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={12}>
                                                        <label className="text-muted small d-block mb-1">Project</label>
                                                        {renderProjectBadges(transaction)}
                                                    </Col>
                                                </Row>
                                            ) : (isPayroll) ? (
                                                <Row className="gy-3">
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">WPS Charge</label>
                                                        <div className="fw-medium text-dark">{formatUSCurrency(transaction?.wps_charge || 0)}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">WPS VAT</label>
                                                        <div className="fw-medium text-dark">{formatUSCurrency(transaction?.wps_vat || 0)}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">Net Payroll</label>
                                                        <div className="fw-bold text-primary">{formatUSCurrency(transaction?.net_payroll_amount || 0)}</div>
                                                    </Col>
                                                    {transaction?.project?.name && (
                                                        <Col md={6}>
                                                            <label className="text-muted small d-block mb-1">Project</label>
                                                            {renderProjectBadges(transaction)}
                                                        </Col>
                                                    )}
                                                </Row>
                                            ) : (isVatPayable) ? (
                                                <Row className="gy-3">
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">VAT Period</label>
                                                        <div className="fw-bold text-dark">{transaction?._rawTransaction?.vat_period || 'N/A'}</div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <label className="text-muted small d-block mb-1">VAT Amount</label>
                                                        <div className="fw-bold text-primary">{formatUSCurrency(vatDetails.totalAmount)}</div>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <div className="text-muted italic py-3 text-center">
                                                    Specific details for this {displayType.toLowerCase()} type are not currently expanded.
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>

                                    {/* Financial Summary Breakdown */}
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <div className="amount-breakdown pe-md-4 border-end">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted small">Base Amount</span>
                                                            <span className="fw-medium">{formatUSCurrency(vatDetails.baseAmount)}</span>
                                                        </div>

                                                        {vatDetails.hasVat && (
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span className="text-muted small">VAT ({vatDetails.vatPercentage}%)</span>
                                                                <span className="text-success small">+ {formatUSCurrency(vatDetails.vatAmount)}</span>
                                                            </div>
                                                        )}

                                                        {(isReceipt || isPayment) && (
                                                            <div className="d-flex justify-content-between mb-2 border-top pt-2">
                                                                <span className="fw-bold small">Document Total</span>
                                                                <span className="fw-bold text-dark">{formatUSCurrency(vatDetails.totalAmount)}</span>
                                                            </div>
                                                        )}

                                                        {deductionAmount > 0 ? (
                                                            <>
                                                                <div className="d-flex justify-content-between mb-1 text-black border-top mt-2 pt-2">
                                                                    <span className="fw-medium small">Bank Charges</span>
                                                                    <span className="fw-medium small"> {formatUSCurrency(deductionAmount)}</span>
                                                                </div>
                                                                
                                                                {/* Breakdown of bank charges - specifically handled by type to avoid duplicate fields */}
                                                                <div className="ps-3 border-start ms-1 mb-2" style={{ borderLeftWidth: '2px !important' }}>
                                                                    {isPayroll ? (
                                                                        <>
                                                                            {parseFloat(transaction?.wps_charge || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">WPS Charge</span>
                                                                                    <span>{formatUSCurrency(transaction.wps_charge)}</span>
                                                                                </div>
                                                                            )}
                                                                            {parseFloat(transaction?.wps_vat || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">WPS VAT</span>
                                                                                    <span>{formatUSCurrency(transaction.wps_vat)}</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (isBankGuarantee || isBankGuaranteeCharges) ? (
                                                                        <>
                                                                            {parseFloat(transaction?.commission_fee || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Commission Fee</span>
                                                                                    <span>{formatUSCurrency(transaction.commission_fee)}</span>
                                                                                </div>
                                                                            )}
                                                                            {parseFloat(transaction?.commission_vat || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Commission VAT</span>
                                                                                    <span>{formatUSCurrency(transaction.commission_vat)}</span>
                                                                                </div>
                                                                            )}
                                                                            {parseFloat(transaction?.bank_service_charge || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Bank Service Charge</span>
                                                                                    <span>{formatUSCurrency(transaction.bank_service_charge)}</span>
                                                                                </div>
                                                                            )}
                                                                            {parseFloat(transaction?.bank_vat_amount || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Bank Charge VAT</span>
                                                                                    <span>{formatUSCurrency(transaction.bank_vat_amount)}</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {parseFloat(transaction?.bank_service_charge || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Service Charge</span>
                                                                                    <span>{formatUSCurrency(transaction.bank_service_charge)}</span>
                                                                                </div>
                                                                            )}
                                                                            {parseFloat(transaction?.bank_vat_amount || 0) > 0 && (
                                                                                <div className="d-flex justify-content-between mb-0 text-danger smallest">
                                                                                    <span className="opacity-80">Charge VAT</span>
                                                                                    <span>{formatUSCurrency(transaction.bank_vat_amount)}</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="mb-2 border-top pt-2"></div>
                                                        )}
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="ps-md-4 text-center d-flex flex-column justify-content-center h-100">
                                                        <div className="font-size-11 text-uppercase fw-bold text-muted mb-1">Total Bank Amount</div>
                                                        <h2 className="mb-0 fw-bold text-primary">{formatUSCurrency(bankAmount)}</h2>

                                                        {Math.abs(expectedBankAmount - bankAmount) > 0.01 && (
                                                            <div className="mt-2 text-center">
                                                                <Badge bg="soft-warning" className="text-warning p-2">
                                                                    Reconciliation Difference: {formatUSCurrency(bankAmount - expectedBankAmount)}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    {/* Reversal Info & Journal Preview - Secondary */}
                                    <Row className="gy-4">
                                        {transaction?.reconciliation_status === 'reversed' && (
                                            <Col md={journalEntry ? 6 : 12}>
                                                <Card className="border-0 shadow-sm h-100 bg-soft-danger border border-danger border-opacity-10">
                                                    <Card.Body>
                                                        <h6 className="text-danger text-uppercase font-size-11 fw-bold mb-3 border-bottom border-danger border-opacity-10 pb-2">Reversal Details</h6>
                                                        <div className="mb-2">
                                                            <label className="text-muted smallest d-block">Reason</label>
                                                            <div className="fw-medium small">{transaction.reversal_reason || 'N/A'}</div>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-end">
                                                            <div>
                                                                <label className="text-muted smallest d-block">Date</label>
                                                                <div className="fw-medium small">{formatDate(transaction.reversal_date)}</div>
                                                            </div>
                                                            <div className="text-end">
                                                                <label className="text-muted smallest d-block">Amount</label>
                                                                <div className="fw-bold text-danger">-{formatUSCurrency(transaction.reverse_amount || transaction.amount)}</div>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {journalEntry && (
                                            <Col md={(transaction?.reconciliation_status === 'reversed') ? 6 : 12}>
                                                <Card className="border-0 shadow-sm h-100">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                                            <h6 className="text-muted text-uppercase font-size-11 fw-bold mb-0">Journal Entry Preview</h6>
                                                            <Badge bg="soft-secondary" className="text-secondary font-size-10">#{journalEntry.id}</Badge>
                                                        </div>
                                                        <Row>
                                                            <Col xs={6}>
                                                                <label className="text-muted smallest d-block">Number</label>
                                                                <div className="fw-medium small text-dark">{journalEntry.number}</div>
                                                            </Col>
                                                            <Col xs={6}>
                                                                <label className="text-muted smallest d-block">Date</label>
                                                                <div className="fw-medium small text-dark">{formatDate(journalEntry.date)}</div>
                                                            </Col>
                                                            <Col xs={12} className="mt-2">
                                                                <label className="text-muted smallest d-block">Narration</label>
                                                                <div className="text-muted font-size-12 line-clamp-2">{journalEntry.notes || 'No narration provided'}</div>
                                                            </Col>
                                                        </Row>

                                                        {/* Detailed Ledger Entries */}
                                                        <div className="mt-4">
                                                            <h6 className="text-muted font-size-11 fw-bold text-uppercase border-bottom pb-1 mb-2">Ledger Details</h6>
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <thead className="bg-light">
                                                                        <tr>
                                                                            <th className="font-size-11 py-1">Account</th>
                                                                            <th className="font-size-11 text-end py-1">Debit</th>
                                                                            <th className="font-size-11 text-end py-1">Credit</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {journalEntry.entryitems && journalEntry.entryitems.map((item, idx) => (
                                                                            <tr key={idx} className="border-bottom-dashed">
                                                                                <td className="font-size-12 py-2">
                                                                                    <div className="fw-medium text-dark">{item.ledger?.name || item.ledger_name || 'N/A'}</div>
                                                                                    {item.narration && <div className="smallest text-muted">{item.narration}</div>}
                                                                                </td>
                                                                                <td className="text-end font-size-12 py-2">
                                                                                    {item.dc === 'D' ? formatUSCurrency(item.amount) : '-'}
                                                                                </td>
                                                                                <td className="text-end font-size-12 py-2">
                                                                                    {item.dc === 'C' ? formatUSCurrency(item.amount) : '-'}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Offcanvas.Body>
                <div className="p-3 border-top bg-white d-flex justify-content-end">
                    <Button variant="outline-secondary" onClick={onHide} className="px-4">Close Details</Button>
                </div>
            </Offcanvas>
        );
    };
    const fetchJournalById = async (journalId, apiKey, apiUrl) => {
        try {
            const response = await axios.get(`${apiUrl}/api/entries/getEntryById`, {
                headers: {
                    'x-api-key': apiKey
                },
                params: {
                    entry_id: journalId
                }
            });

            const baseEntry = response.data?.entry || null;
            const relatedEntry = response.data?.related_entry || null;

            return baseEntry
                ? { ...baseEntry, related_entry: relatedEntry }
                : null;

        } catch (error) {
            console.error("Error fetching journal entry:", error);
            return null;
        }
    };
    // Add these helper functions after your imports

    // Format number to US format with commas
    const formatUSCurrency = (amount) => {
        if (!amount && amount !== 0) return '0.00';
        return parseFloat(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Get appropriate currency for transaction
    const getTransactionCurrency = (txn) => {
        // Helper to extract currency from account object
        const extractCurrencyFromAccount = (account) => {
            if (!account) return null;

            // If account is a string, return it
            if (typeof account === 'string') return account;

            // If account is an object with currency property
            if (account.currency && typeof account.currency === 'string') {
                return account.currency;
            }

            // If account.currency is an object with iso3 or currency_code
            // if (account.currency && typeof account.currency === 'object') {
            //     return account.currency.iso3 ||
            //            account.currency.currency_code || 
            //            account.currency.currency || 
            //            null;
            // }

            return null;
        };

        if (txn.entrytype_id === 1) { // Income
            // Use client currency or invoice currency
            return txn.invoice?.currency ||
                txn.client?.currency ||
                extractCurrencyFromAccount(txn.account) ||
                'AED';
        } else if (txn.entrytype_id === 2) { // Expense
            // Use vendor currency or expense currency
            return txn.expense?.vendor_currency || txn?.transaction_expense_items?.expense?.vendor_currency ||

                extractCurrencyFromAccount(txn.account) ||
                'USD';
        }

        return extractCurrencyFromAccount(txn.account) || 'AED';
    };


    const stableTransaction = useMemo(
        () => selectedTransaction,
        [selectedTransaction?.journal_id]
    );

    // Add to your existing state declarations
    const [showReverseModal, setShowReverseModal] = useState(false);
    const [transactionToReverse, setTransactionToReverse] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [loadingReverse, setLoadingReverse] = useState(false);

    // Add this function to handle reverse
    const handleReverseClick = (transaction) => {
        setTransactionToReverse(transaction);
        setReversalReason('');
        setShowReverseModal(true);
    };

    // Add the reverse reconciliation function
    const reverseReconciliation = async () => {
        if (!transactionToReverse || !reversalReason.trim()) {
            alert('Please provide a reason for reversal');
            return;
        }

        setLoadingReverse(true);
        try {
            const payload = {
                transaction_id: transactionToReverse.id,
                entry_id: transactionToReverse.entry_id,
                reason: reversalReason,
                reversed_by: userInfo?.id || userInfo?.user_id,
                reversal_date: new Date().toISOString()
            };

            console.log('Reversal Payload:', payload);

            const response = await axios.post(`${backendUrl}/api/reverse-reconciliation`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                alert('Reconciliation reversed successfully');
                setShowReverseModal(false);
                await fetchBankTransactions(); // Refresh the list

                // Also refresh pending items
                const [expensesRes, invoicesRes] = await Promise.all([
                    axios.get(`${backendUrl}/api/expense?reconciliation_status=pending`, {
                        headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin }
                    }),
                    axios.get(`${backendUrl}/api/proformainvoice?reconciliation_status=pending`, {
                        headers: { Authorization: `Bearer ${token}`, "Tenant-Origin": origin }
                    })
                ]);

                setPendingExpenses(expensesRes.data?.data || []);
                setPendingInvoices(invoicesRes.data?.proformainvoice || []);
            } else {
                throw new Error(response.data.message || 'Reversal failed');
            }
        } catch (error) {
            console.error("Reversal error:", error.response?.data || error.message);
            alert(`Reversal failed: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoadingReverse(false);
        }
    };


    const ReverseReconciliationModal = ({ show, onHide, transaction }) => {
        const [reason, setReason] = useState('');
        const [loading, setLoading] = useState(false);
        const [reverseDate, setReverseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
        const [reverseAmount, setReverseAmount] = useState('');
        const [reverseDeduction, setReverseDeduction] = useState('');
        const [reverseServiceCharge, setReverseServiceCharge] = useState('');
        const [reverseVat, setReverseVat] = useState('');
        const [bankCharge, setBankCharge] = useState(''); // For petty cash reversals
        const [selectedExpenses, setSelectedExpenses] = useState([]); // For multi expense selection

        // Check if this is a petty cash payment
        const isPettyCashPayment = transaction?.payment_mode === 'petty_cash' || transaction?.user_id;
        const hasMultipleExpenses = transaction?.transaction_expense_items?.length > 1;

        useEffect(() => {
            if (transaction) {
                setReverseAmount(transaction.convert_amount || transaction.amount || '');
                setReverseDeduction(transaction.deduction_amount || '');
                setReverseServiceCharge('');
                setReverseVat('');
                setBankCharge('');

                // If petty cash with single expense, auto-select it
                if (isPettyCashPayment && transaction?.transaction_expense_items?.length === 1) {
                    setSelectedExpenses([transaction.transaction_expense_items[0].expense_id]);
                } else {
                    setSelectedExpenses([]);
                }
            }
        }, [transaction, isPettyCashPayment]);

        const showToast = (type, message) => {
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', Swal.stopTimer);
                    toast.addEventListener('mouseleave', Swal.resumeTimer);
                }
            });

            Toast.fire({
                icon: type,
                title: message,
                background: type === 'success' ? '#ffffff' : '#ffffff',
                color: type === 'success' ? '#155724' : '#721c24'
            });
        };

        const handleExpenseToggle = (expenseId) => {
            let nextSelected;
            if (selectedExpenses.includes(expenseId)) {
                nextSelected = selectedExpenses.filter(id => id !== expenseId);
            } else {
                nextSelected = [...selectedExpenses, expenseId];
            }
            setSelectedExpenses(nextSelected);
            updateReverseAmount(nextSelected);
        };

        const updateReverseAmount = (selectedIds) => {
            if (transaction?.transaction_expense_items) {
                const total = transaction.transaction_expense_items
                    .filter(item => selectedIds.includes(item.expense_id))
                    .reduce((sum, item) => sum + parseFloat(item.paid_amount || item.expense?.total_amount || 0), 0);
                setReverseAmount(total > 0 ? total.toFixed(2) : '');
            }
        };

        const handleSelectAllExpenses = () => {
            const allIds = transaction.transaction_expense_items.map(item => item.expense_id);
            setSelectedExpenses(allIds);
            updateReverseAmount(allIds);
        };

        const handleDeselectAllExpenses = () => {
            setSelectedExpenses([]);
            setReverseAmount('');
        };

        // Calculate net reverse amount (reverse amount - bank charge)
        const netReverseAmount = parseFloat(reverseAmount || 0) - parseFloat(bankCharge || 0);

        const handleReverse = async () => {
            // Validation for petty cash payments
            if (isPettyCashPayment && hasMultipleExpenses && selectedExpenses.length === 0) {
                showToast('error', 'Please select at least one expense to reverse');
                return;
            }

            if (!reason.trim()) {
                showToast('error', 'Please provide a reason for reversal');
                return;
            }

            setLoading(true);
            try {
                const payload = {
                    transaction_id: transaction.id,
                    reverse_amount: parseFloat(reverseAmount) || transaction.amount,
                    reverse_deduction: parseFloat(reverseDeduction) || 0,
                    reverse_service_charge: parseFloat(reverseServiceCharge) || 0,
                    reverse_vat: parseFloat(reverseVat) || 0,
                    reverse_date: reverseDate,
                    reason,
                    // Petty cash specific fields
                    is_petty_cash: isPettyCashPayment,
                    bank_charge: parseFloat(bankCharge) || 0,
                    net_reverse_amount: netReverseAmount,
                    user_id: transaction.user_id,
                    user_name: transaction.user?.name,
                    selected_expense_ids: isPettyCashPayment && hasMultipleExpenses ? selectedExpenses : [],
                };

                const response = await axios.post(
                    `${backendUrl}/api/transactions/${transaction.id}/reverse`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Tenant-Origin": origin
                        }
                    }
                );

                if (response.data.success) {
                    showToast('success', 'Transaction reversed successfully!');
                    onHide();
                    await fetchBankTransactions();
                } else {
                    showToast('error', response.data.message || 'Reversal failed');
                }
            } catch (error) {
                console.error('Reversal error:', error);
                showToast('error', 'Error reversing transaction: ' + (error.response?.data?.message || error.message));
            } finally {
                setLoading(false);
            }
        };

        // Check if already reversed
        const isAlreadyReversed = transaction?.reconciliation_status === 'reversed';

        return (
            <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '600px' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        {isAlreadyReversed ? 'Reversal Details' : 'Reverse Transaction'}
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="d-flex flex-column">
                    {isAlreadyReversed ? (
                        // Show reversal details if already reversed
                        <div>
                            <Alert variant="info">
                                <strong>Already Reversed</strong>
                                <p className="mb-0 mt-2">
                                    This transaction was already reversed on {format(new Date(transaction.reversal_date), 'dd MMM yyyy')}
                                </p>
                            </Alert>

                            <Card className="mb-3">
                                <Card.Body>
                                    <h6>Reversal Details</h6>
                                    <div className="reversal-details">
                                        <div className="detail-row mb-2 d-flex justify-content-between border-bottom pb-1">
                                            <span className="detail-label text-muted">Reversal Date:</span>
                                            <span className="fw-bold">{transaction.reversal_date ? format(new Date(transaction.reversal_date), 'dd MMM yyyy') : 'N/A'}</span>
                                        </div>
                                        <div className="detail-row mb-2 d-flex justify-content-between border-bottom pb-1">
                                            <span className="detail-label text-muted">Total Reversal (Gross):</span>
                                            <span className="fw-bold">{formatUSCurrency(transaction.reverse_amount || transaction.amount)}</span>
                                        </div>
                                        <div className="detail-row mb-2 d-flex justify-content-between border-bottom pb-1">
                                            <span className="detail-label text-muted">Bank Charges & VAT:</span>
                                            <span className="text-danger">
                                                {formatUSCurrency(parseFloat(transaction.reverse_service_charge || 0) + parseFloat(transaction.reverse_vat || 0))}
                                            </span>
                                        </div>
                                        <div className="detail-row mb-2 d-flex justify-content-between border-bottom pb-1 bg-light p-1">
                                            <span className="detail-label fw-bold">Bank Refund (Net):</span>
                                            <span className="fw-bold text-success">
                                                {formatUSCurrency(
                                                    parseFloat(transaction.reverse_amount || transaction.amount) -
                                                    (parseFloat(transaction.reverse_service_charge || 0) + parseFloat(transaction.reverse_vat || 0))
                                                )}
                                            </span>
                                        </div>
                                        {transaction.reverse_deduction > 0 && (
                                            <div className="detail-row mb-2 d-flex justify-content-between border-bottom pb-1">
                                                <span className="detail-label text-muted">Deduction:</span>
                                                <span className="text-danger">{formatUSCurrency(transaction.reverse_deduction)}</span>
                                            </div>
                                        )}
                                        <div className="detail-row mb-2">
                                            <span className="detail-label text-muted d-block mb-1">Reason:</span>
                                            <div className="p-2 bg-light border rounded small">
                                                {transaction.reversal_reason || 'No reason provided'}
                                            </div>
                                        </div>
                                        <div className="detail-row mt-3 text-end">
                                            <small className="text-muted">Reversed By User ID: {transaction.reversed_by}</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            <Button variant="outline-secondary" onClick={onHide}>
                                Close
                            </Button>
                        </div>
                    ) : (
                        // Show reversal form if not reversed yet
                        <>
                            {/* Petty Cash Payment Info */}
                            {isPettyCashPayment && (
                                <Alert variant="info" className="mb-3">
                                    <strong>Petty Cash Reversal</strong>

                                </Alert>
                            )}

                            {/* Expense Selection for Multi-Expense Petty Cash Payments */}
                            {isPettyCashPayment && hasMultipleExpenses && (
                                <Form.Group className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="fw-bold mb-0">Select Expenses to Reverse *</Form.Label>
                                        <div className="btn-group btn-group-sm">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleSelectAllExpenses}
                                                className="py-0"
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={handleDeselectAllExpenses}
                                                className="py-0"
                                            >
                                                Deselect All
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {transaction.transaction_expense_items.map((item, index) => (
                                            <Form.Check
                                                key={item.expense_id || index}
                                                type="checkbox"
                                                id={`expense-${item.expense_id}`}
                                                label={
                                                    <div className="d-flex justify-content-between align-items-center w-100">
                                                        <span>
                                                            {item.expense?.narration && (
                                                                <small className="text-muted d-block">{item.expense.narration.substring(0, 50)}</small>
                                                            )}
                                                        </span>
                                                        <span className="fw-bold"> ({item.paid_amount || item.expense?.total_amount || 0}) </span>
                                                    </div>
                                                }
                                                checked={selectedExpenses.includes(item.expense_id)}
                                                onChange={() => handleExpenseToggle(item.expense_id)}
                                                className="mb-2"
                                            />
                                        ))}
                                    </div>
                                    <Form.Text className="text-muted">
                                        Select the expense(s) you want to reverse
                                    </Form.Text>
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Reversal Date *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={reverseDate}
                                    onChange={(e) => setReverseDate(e.target.value)}
                                    disabled={loading}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Amount *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={reverseAmount}
                                    onChange={(e) => setReverseAmount(e.target.value)}
                                    disabled={loading}
                                />

                            </Form.Group>

                            {/* Bank Charge for Petty Cash */}
                            {isPettyCashPayment && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Bank Charge</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={bankCharge}
                                        onChange={(e) => setBankCharge(e.target.value)}
                                        disabled={loading}
                                    />

                                </Form.Group>
                            )}

                            {transaction?.type === 'payment' && !isPettyCashPayment ? (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Bank Service Charge</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={reverseServiceCharge}
                                            onChange={(e) => setReverseServiceCharge(e.target.value)}
                                            placeholder="Enter bank service charge"
                                            disabled={loading}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>VAT on Bank Charges</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={reverseVat}
                                            onChange={(e) => setReverseVat(e.target.value)}
                                            placeholder="Enter VAT on bank charges"
                                            disabled={loading}
                                        />
                                    </Form.Group>
                                </>
                            ) : !isPettyCashPayment && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Bank Deduction/Charges</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={reverseDeduction}
                                        onChange={(e) => setReverseDeduction(e.target.value)}
                                        disabled={loading}
                                    />
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Description *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </Form.Group>

                            <div className="d-grid gap-2 mt-auto">
                                <Button
                                    variant="danger"
                                    onClick={handleReverse}
                                    disabled={loading || !reason.trim()}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        'Reverse Transaction'
                                    )}
                                </Button>
                                <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        );
    };


    const handleApply = () => {
        if (selectedBank) {
            // Convert dd-MM-yyyy to yyyy-MM-dd for backend
            const backendStartDate = formatDateForBackend(startDate);
            const backendEndDate = formatDateForBackend(endDate);
            fetchBankTransactions(backendStartDate, backendEndDate);
        }
    };

    const clearDateFilters = () => {
        setStartDate('');
        setEndDate('');
        setFilters({
            date: '',
            amount: '',
            reference: '',
            status: 'all',
            dc: 'all'
        });
        setTransactionTypeFilter('');
        if (selectedBank) {
            fetchBankTransactions();
        }
    };

    const getTransactionDC = (txn) => {
        if (!txn) return 'other';

        // 1. Check explicit DC flag from accounting entry (source of truth)
        const explicitDC = (txn.dc || (txn.entry_item && txn.entry_item.dc) || '').toString().trim().toUpperCase();
        if (explicitDC === 'D') return 'debit';
        if (explicitDC === 'C') return 'credit';

        // 2. Check raw type strings (case-insensitive)
        const rawType = (txn._rawTransaction?.type || txn.type || '').toString().toLowerCase();

        // 3. Check entrytype_id (1 = Receipt/Debit, 2 = Payment/Credit) - use loose equality for string/number comparison
        const typeId = txn.entrytype_id;
        if (typeId == 1) return 'debit';
        if (typeId == 2) return 'credit';

        const debitTypes = ['income', 'receipt', 'capital', 'bank_guarantee_released', 'deposit', 'bank receipt', 'interest'];
        const creditTypes = ['expense', 'payment', 'payroll', 'vat_payable', 'withdrawal', 'bank_guarantee', 'bank_guarantee_charges', 'bad_debt_written_off', 'bank payment', 'charges'];

        if (debitTypes.includes(rawType)) return 'debit';
        if (creditTypes.includes(rawType)) return 'credit';

        // 4. Handle Advance based on direction
        if (rawType === 'advance') {
            const advType = (txn.advance_type || '').toLowerCase();
            return advType === 'client' ? 'debit' : 'credit';
        }

        // 5. Handle Contra/Transfers specifically
        if (rawType === 'contra' || txn.is_contra) {
            if (txn.isSelectedTo) return 'debit';
            if (txn.isSelectedFrom) return 'credit';
        }

        return 'other';
    };

    const applyFilters = (transaction) => {
        const txnDC = getTransactionDC(transaction);
        return (
            (!filters.date || transaction.date.includes(filters.date)) &&
            (!filters.amount || transaction.amount.toString().includes(filters.amount)) &&
            (!filters.reference ||
                (transaction.reference && transaction.reference.toLowerCase().includes(filters.reference.toLowerCase())) ||
                (transaction.description && transaction.description.toLowerCase().includes(filters.reference.toLowerCase()))) &&
            (filters.status === 'all' ||
                (filters.status === 'matched' && transaction.reconciliation_status === 'reconciled') ||
                (filters.status === 'unmatched' && transaction.reconciliation_status == 'pending')) &&
            (!transactionTypeFilter ||
                transaction.type === transactionTypeFilter ||
                transaction._rawTransaction?.type === transactionTypeFilter ||
                transaction.payment_mode === transactionTypeFilter) &&
            (filters.dc === 'all' || filters.dc === txnDC)
        );
    };

    const filteredTransactions = bankTransactions.filter(applyFilters).sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
        }

        const createdA = new Date(a.created_at || a._rawTransaction?.created_at || 0);
        const createdB = new Date(b.created_at || b._rawTransaction?.created_at || 0);

        return createdA - createdB;
    });
    
    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(0);
    }, [filters, transactionTypeFilter, selectedBank]);

    const pageCount = Math.ceil(filteredTransactions.length / itemsPerPage);
    const offset = currentPage * itemsPerPage;
    const paginatedTransactions = filteredTransactions.slice(offset, offset + itemsPerPage);

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Check if any filter is active
    const isFiltered = useMemo(() => {
        return !!transactionTypeFilter || !!filters.reference || !!filters.date || filters.status !== 'all' || filters.dc !== 'all';
    }, [transactionTypeFilter, filters.reference, filters.date, filters.status, filters.dc]);

    // Calculate summary totals based on filtered list
    const filteredSummary = useMemo(() => {
        let debitTotal = 0;
        let creditTotal = 0;

        filteredTransactions.forEach(txn => {
            const txnDC = getTransactionDC(txn);
            const isDebit = txnDC === 'debit';
            const isCredit = txnDC === 'credit';

            if (isDebit) {
                debitTotal += parseFloat(txn.amount || 0);
            } else if (isCredit) {
                creditTotal += parseFloat(txn.amount || 0);
            }

            // Handle VAT/Charges if applicable (though usually included in amount for bank reconciliation)
            // ... any specific VAT logic if needed ...
        });

        return {
            debit: debitTotal,
            credit: creditTotal,
            vat: 0, // Placeholder if vat is not being calculated here anymore
            net: debitTotal - creditTotal,
            debitCount: filteredTransactions.filter(t => getTransactionDC(t) === 'debit').length,
            creditCount: filteredTransactions.filter(t => getTransactionDC(t) === 'credit').length
        };
    }, [filteredTransactions]);

    // Calculate summary totals
    const summaryTotals = {
        matched: filteredTransactions
            .filter(t => t.reconciliation_status === 'reconciled')
            .reduce((sum, t) => sum + parseFloat(t.amount), 0),
        unmatched: filteredTransactions
            .filter(t => t.reconciliation_status == 'pending')
            .reduce((sum, t) => sum + parseFloat(t.amount), 0)
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    {/* Page Title */}
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Bank Reconciliation</h4>
                            </div>
                        </div>
                    </div>

                    <div className="bank-reconciliation-report">
                        {/* Header Section */}


                        {/* Control Panel */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Body>
                                <Row>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Bank Account</Form.Label>
                                            <Form.Select
                                                value={selectedBank}
                                                onChange={(e) => setSelectedBank(e.target.value)}
                                                disabled={loading}
                                            >
                                                <option>Select Bank</option>
                                                {bankAccounts?.filter(account => ['bank account', 'cash account'].includes(account.type?.toLowerCase())).map(account => (
                                                    <option key={account.id} value={account.id}>
                                                        {account.name} ({account.base_currency?.currency || (typeof account.currency === 'object' ? account.currency.currency : account.currency)})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Start Date</Form.Label>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    views={["year", "month", "day"]}
                                                    format="DD-MM-YYYY"
                                                    value={startDate ? dayjs(startDate) : null}
                                                    onChange={(newValue) => {
                                                        const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate);
                                                        setStartDate(newStart);
                                                        if (suggestedEndDate) {
                                                            setEndDate(suggestedEndDate);
                                                        }
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small",
                                                            fullWidth: true,
                                                            variant: "outlined",
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>End Date</Form.Label>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    views={["year", "month", "day"]}
                                                    format="DD-MM-YYYY"
                                                    value={endDate ? dayjs(endDate) : null}
                                                    onChange={(newValue) => {
                                                        setEndDate(getAdjustedEndDate(newValue, startDate));
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small",
                                                            fullWidth: true,
                                                            variant: "outlined",
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Form.Group>
                                    </Col>
                                    {/* <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Reconciliation Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={reconciliationDate}
                                                onChange={(e) => setReconciliationDate(e.target.value)}
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col> */}
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Status Filter</Form.Label>
                                            <Form.Select
                                                value={filters.status}
                                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                                disabled={loading}
                                            >
                                                <option value="all">All Transactions</option>
                                                <option value="matched">Reconciled Only</option>
                                                <option value="unmatched">Non-Reconciled Only</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Cash In/Out</Form.Label>
                                            <Form.Select
                                                value={filters.dc}
                                                onChange={(e) => setFilters({ ...filters, dc: e.target.value })}
                                                disabled={loading}
                                            >
                                                <option value="all">All (In/Out)</option>
                                                <option value="debit">Cash In (Debit)</option>
                                                <option value="credit">Cash Out (Credit)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Transaction Type</Form.Label>
                                            <Form.Select
                                                value={transactionTypeFilter}
                                                onChange={(e) => setTransactionTypeFilter(e.target.value)}
                                                disabled={loading}
                                            >
                                                <option value="">All Types</option>
                                                <option value="advance">Advance</option>
                                                <option value="bad_debt_written_off">Bad Debt Written Off</option>
                                                <option value="bank_guarantee">Bank Guarantee Issued</option>
                                                <option value="bank_guarantee_charges">Bank Guarantee Charges</option>
                                                <option value="bank_guarantee_released">Bank Guarantee Released</option>
                                                <option value="capital">Capital</option>
                                                <option value="contra">Contra</option>
                                                <option value="expense"> Expense</option>
                                                <option value="income">Income</option>
                                                <option value="payroll">Payroll</option>
                                                <option value="vat_payable">VAT Payment</option>
                                                <option value="withdrawal">Withdrawal</option>
                                                <option value="petty_cash">Petty Cash</option>

                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <Col md={9}>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FiSearch />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search by reference, amount, or date"
                                                value={filters.reference}
                                                onChange={(e) => setFilters({ ...filters, reference: e.target.value })}
                                                disabled={loading}
                                            />
                                        </InputGroup>
                                    </Col>
                                    <Col md={3} className="d-flex align-items-end gap-2">
                                        <Button
                                            variant="primary"
                                            onClick={handleApply}
                                            disabled={loading || !selectedBank}
                                            className="w-50"
                                        >
                                            <FiSearch className="me-1" />
                                            Apply
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={clearDateFilters}
                                            disabled={loading}
                                            className="w-50"
                                        >
                                            <FiX className="me-1" />
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Financial Summary Cards */}
                        {selectedBank && (
                            <Row className="mb-4">
                                <Col md={3}>
                                    <Card className="shadow-sm border-0 bg-light h-100">
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <h6 className="text-muted small mb-0 text-uppercase fw-bold">Opening Balance</h6>
                                                {isFiltered && <Badge bg="soft-secondary" className="ms-auto font-size-10">Period</Badge>}
                                            </div>
                                            <div className={`h5 mb-0 ${ledgerSummary?.openingBalance?.dc === 'D' ? 'text-danger' : 'text-success'}`}>
                                                {ledgerSummary?.openingBalance ? (
                                                    <>
                                                        {ledgerSummary.openingBalance.dc === 'D' ? 'Dr' : 'Cr'} {parseFloat(ledgerSummary.openingBalance.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                    </>
                                                ) : '0.00'}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className={`shadow-sm border-0 h-100 border-start border-4 ${isFiltered ? 'bg-soft-success' : 'bg-light'}`}>
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <h6 className="text-muted small mb-0 text-uppercase fw-bold">
                                                    {isFiltered ? 'Total Debit' : 'Total Debit (In)'}
                                                </h6>
                                                {isFiltered && (
                                                    <span className="ms-auto avatar-xs bg-success text-white rounded-circle d-flex align-items-center justify-content-center font-size-10">
                                                        {filteredSummary.debitCount}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="h5 mb-0 text-success">
                                                {isFiltered
                                                    ? formatUSCurrency(filteredSummary.debit)
                                                    : parseFloat(ledgerSummary?.periodTotals?.dr_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                                }
                                            </div>
                                            {isFiltered && (
                                                <div className="text-muted smallest mt-1">
                                                    of {parseFloat(ledgerSummary?.periodTotals?.dr_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} total
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className={`shadow-sm border-0 h-100 border-start border-4 ${isFiltered ? 'bg-soft-danger' : 'bg-light'}`}>
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <h6 className="text-muted small mb-0 text-uppercase fw-bold">
                                                    {isFiltered ? 'Total Credit' : 'Total Credit (Out)'}
                                                </h6>
                                                {isFiltered && (
                                                    <span className="ms-auto avatar-xs bg-danger text-white rounded-circle d-flex align-items-center justify-content-center font-size-10">
                                                        {filteredSummary.creditCount}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="h5 mb-0 text-danger">
                                                {isFiltered
                                                    ? formatUSCurrency(filteredSummary.credit)
                                                    : parseFloat(ledgerSummary?.periodTotals?.cr_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                                }
                                            </div>
                                            {isFiltered && (
                                                <div className="text-muted smallest mt-1">
                                                    of {parseFloat(ledgerSummary?.periodTotals?.cr_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} total
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                                {/* {transactionTypeFilter === 'petty_cash' && (
                                    <Col md={3}>
                                        <Card className="shadow-sm border-0 h-100 border-start border-4 bg-soft-info">
                                            <Card.Body className="p-3">
                                                <h6 className="text-muted small mb-1 text-uppercase fw-bold">Total VAT (5%)</h6>
                                                <div className="h5 mb-0 text-info">
                                                    {formatUSCurrency(filteredSummary.vat)}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                )} */}
                                <Col md={3}>
                                    <Card className={`shadow-sm border-0 h-100 border-start border-4 ${isFiltered ? (filteredSummary.net >= 0 ? 'bg-soft-primary' : 'bg-soft-warning') : 'bg-light bg-opacity-10'}`}>
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center mb-1">
                                                <h6 className="text-muted small mb-0 text-uppercase fw-bold">
                                                    {isFiltered ? 'Net Movement' : 'Closing Balance'}
                                                </h6>
                                            </div>
                                            <div className={isFiltered ? (filteredSummary.net >= 0 ? 'h5 mb-0 text-primary' : 'h5 mb-0 text-warning') : `h5 mb-0 ${ledgerSummary?.closingBalance?.dc === 'D' ? 'text-danger' : 'text-success'}`}>
                                                {isFiltered ? (
                                                    <>
                                                        {filteredSummary.net >= 0 ? '+' : ''}{formatUSCurrency(filteredSummary.net)}
                                                    </>
                                                ) : ledgerSummary?.closingBalance ? (
                                                    <>
                                                        {ledgerSummary.closingBalance.dc === 'D' ? 'Dr' : 'Cr'} {parseFloat(ledgerSummary.closingBalance.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                    </>
                                                ) : '0.00'}
                                            </div>
                                            {isFiltered && (
                                                <div className="text-muted smallest mt-1">
                                                    {filteredTransactions.length} total items
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {/* Summary Cards */}
                        {/* <Row className="mb-4">
                            <Col md={6}>
                                <Card className="summary-card matched">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-uppercase text-muted">Reconciled</h6>
                                                <h3 className="mb-0">
                                                    {filteredTransactions.filter(t => t.reconciliation_status === 'reconciled').length} Transactions
                                                </h3>
                                            </div>
                                            <div className="icon-circle bg-success">
                                                <FiCheckCircle size={24} className="text-white" />
                                            </div>
                                        </div>
                                        <p className="mt-3 mb-0">
                                            Total:  {formatUSCurrency(summaryTotals.matched)}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className="summary-card unmatched">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-uppercase text-muted">Non-Reconciled</h6>
                                                <h3 className="mb-0">
                                                    {filteredTransactions.filter(t => t.reconciliation_status == 'pending').length} Transactions
                                                </h3>
                                            </div>
                                            <div className="icon-circle bg-danger">
                                                <FiAlertCircle size={24} className="text-white" />
                                            </div>
                                        </div>
                                        <p className="mt-3 mb-0">
                                            Total:  {formatUSCurrency(summaryTotals.unmatched)}
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row> */}

                        {/* Transactions Table */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <h5 className="mb-0">Bank Transactions</h5>
                            </Card.Header>
                            <Card.Body className="p-0">

                                <div className="table-responsive">
                                    <table className="table align-middle table-nowrap table-check table-list">
                                        <thead className="table-sales-gradient">
                                            <tr>
                                                <th style={{ width: '20px' }} className="align-middle">
                                                    <div className="form-check font-size-16">
                                                        <input className="form-check-input" type="checkbox" id="checkAll" />
                                                        <label className="form-check-label" htmlFor="checkAll"></label>
                                                    </div>
                                                </th>
                                                <th className="align-middle">#</th>
                                                <th>Description</th>
                                                <th>Date</th>


                                                <th>Vendor/Client</th>
                                                <th>Reference</th>

                                                <th >Amount</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedTransactions.map((txn, idx) => (
                                                <tr
                                                    key={`txn-${idx}`}
                                                    className={txn.reconciliation_status === 'reconciled' ? 'table-white' : 'table-white'}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <div className="form-check font-size-16">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`transactionCheck${idx}`}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`transactionCheck${idx}`}
                                                            ></label>
                                                        </div>
                                                    </td>
                                                    <td>{offset + idx + 1}</td>

                                                    <td className='text-start'>
                                                        {txn.description || ''}

                                                    </td>


                                                    <td>{formatDateForDisplay(txn.date)}</td>

                                                    <td>
                                                        {(() => {
                                                            const rawType = txn._rawTransaction?.type || txn.type;
                                                            if (rawType === 'contra') {
                                                                const isTo = txn.isSelectedTo;
                                                                const isFrom = txn.isSelectedFrom;

                                                                if (isTo) {
                                                                    return (
                                                                        <div className="d-flex flex-column">
                                                                            <span className="text-success fw-bold">Receipt (Debit)</span>
                                                                            <small className="text-muted">From: {txn.from_bank_name || ''}</small>
                                                                        </div>
                                                                    );
                                                                } else if (isFrom) {
                                                                    return (
                                                                        <div className="d-flex flex-column">
                                                                            <span className="text-danger fw-bold">Payment (Credit)</span>
                                                                            <small className="text-muted">To: {txn.to_bank_name || ''}</small>
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <div className="d-flex flex-column">
                                                                        <small className="text-muted">From: {txn.from_bank_name || ''}</small>
                                                                        <small className="text-muted">To: {txn.to_bank_name || ''}</small>
                                                                    </div>
                                                                );
                                                            }
                                                            if (rawType === 'vat_payable') return <span className="text-muted fst-italic"></span>;
                                                            return txn.entrytype_id === 2
                                                                ? (txn.vendor?.name)
                                                                : (txn.client?.name);
                                                        })()}
                                                    </td>
                                                    <td>{txn.reference || ''}</td>

                                                    {/* <td className="text-end">{txn.amount}</td> */}
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            {(() => {
                                                                const txnDC = getTransactionDC(txn);
                                                                if (txnDC === 'debit') {
                                                                    return <FiArrowDownLeft className="text-success me-2 fw-bold" title="Money In (Debit)" />;
                                                                } else if (txnDC === 'credit') {
                                                                    return <FiArrowUpRight className="text-danger me-2 fw-bold" title="Money Out (Credit)" />;
                                                                }
                                                                return <FiArrowRight className="text-muted me-2" />;
                                                            })()}
                                                            <span className="fw-bold">
                                                                {formatUSCurrency(txn.amount)}
                                                                <span className="text-muted ms-1 font-size-11">
                                                                    ({txn.currency || ''})
                                                                </span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    {/* <td>{txn.entrytype_id === 1 ? 'Income' : 'Expense'}</td> */}
                                                    <td>
                                                        {console.log('Transaction type check:', {
                                                            id: txn.id,
                                                            type: txn.type,
                                                            rawType: txn._rawTransaction?.type,
                                                            entrytype_id: txn.entrytype_id
                                                        })}

                                                        {(() => {
                                                            const rawType = txn._rawTransaction?.type || txn.type;

                                                            if (rawType === 'advance') {
                                                                return 'Advance';
                                                            } else if (rawType === 'contra') {
                                                                return 'Contra';
                                                            } else if (rawType === 'vat_payable') {
                                                                return 'VAT Payment';
                                                            } else if (rawType === 'withdrawal') {
                                                                return 'Withdrawal';
                                                            } else if (rawType === 'payroll' || txn.is_payroll) {
                                                                return 'Payroll';
                                                            } else if (rawType === 'capital') {
                                                                return 'Capital';
                                                            } else if (rawType === 'bank_guarantee') {
                                                                return 'Bank Guarantee';
                                                            } else if (rawType === 'bank_guarantee_released') {
                                                                return 'BG Released';
                                                            } else if (rawType === 'bank_guarantee_charges') {
                                                                return 'BG Charges';
                                                            } else if (rawType === 'bad_debt_written_off') {
                                                                return 'Bad Debt W.O';
                                                            } else if (rawType === 'expense' || txn.entrytype_id === 2) {
                                                                return 'Expense';
                                                            } else if (rawType === 'income' || txn.entrytype_id === 1) {
                                                                return 'Income';
                                                            } else {
                                                                return 'Other';
                                                            }
                                                        })()}
                                                    </td>
                                                    {/* <td>
                                                        {txn.reconciliation_status === 'reconciled' ? (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 border-0 shadow-none text-primary"
                                                                onClick={() => {
                                                                    setSelectedTransaction(txn);
                                                                    setShowFullView(true);
                                                                }}
                                                            >
                                                                <FiEye />
                                                            </Button>

                                                        ) : (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleReconcileClick(txn)}
                                                            >


                                                                <FiCornerUpLeft className="me-1" />
                                                                Reconcile
                                                            </Button>
                                                        )}
                                                    </td> */}
                                                    <td>
                                                        <Badge
                                                            bg={
                                                                txn.is_reversal_row ? 'info' :
                                                                    txn.reconciliation_status === 'reconciled' ? 'success' :
                                                                        txn.reconciliation_status === 'reversed' ? 'secondary' : 'warning'
                                                            }
                                                            pill
                                                        >
                                                            {txn.is_reversal_row ? (
                                                                <>
                                                                    <FiRefreshCw className="me-1" />
                                                                    Reversal
                                                                </>
                                                            ) : txn.reconciliation_status === 'reconciled' ? (
                                                                <>
                                                                    <FiCheckCircle className="me-1" />
                                                                    Reconciled
                                                                </>
                                                            ) : txn.reconciliation_status === 'reversed' ? (
                                                                <>
                                                                    <FiCornerUpRight className="me-1" />
                                                                    Reversed
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FiAlertCircle className="me-1" />
                                                                    Unmatched
                                                                </>
                                                            )}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="dropdown">
                                                            <i
                                                                className="mdi mdi-dots-horizontal font-size-18"
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                                style={{ cursor: 'pointer' }}
                                                            ></i>
                                                            <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => {
                                                                            setSelectedTransaction(txn);
                                                                            setShowFullView(true);
                                                                        }}
                                                                    >
                                                                        <FiEye className="me-2" />
                                                                        View Details
                                                                    </button>
                                                                </li>

                                                                {txn.is_reversal_row ? (
                                                                    <li>
                                                                        <span className="dropdown-item text-muted" style={{ cursor: 'not-allowed' }}>
                                                                            <FiRefreshCw className="me-2" />
                                                                            Reversal Movement
                                                                        </span>
                                                                    </li>
                                                                ) : txn.reconciliation_status === 'reconciled' ? (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item text-warning"
                                                                            onClick={() => handleReverseClick(txn)}
                                                                        >
                                                                            <FiCornerUpRight className="me-2" />
                                                                            Reverse
                                                                        </button>
                                                                    </li>
                                                                ) : txn.reconciliation_status === 'reversed' ? (
                                                                    <li>
                                                                        <span className="dropdown-item text-muted" style={{ cursor: 'not-allowed' }}>
                                                                            <FiCornerUpRight className="me-2" />
                                                                            Already Reversed
                                                                        </span>
                                                                    </li>
                                                                ) : (
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item text-danger"
                                                                            onClick={() => handleReconcileClick(txn)}
                                                                        >
                                                                            <FiCornerUpLeft className="me-2" />
                                                                            Reconcile
                                                                        </button>
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div className="text-muted small">
                                        Showing {filteredTransactions.length > 0 ? offset + 1 : 0} to {Math.min(offset + itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
                                    </div>
                                    
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <small className="text-muted">Show:</small>
                                            <select 
                                                className="form-select form-select-sm" 
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(0);
                                                }}
                                            >
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                        </div>

                                        {pageCount > 1 && (
                                            <ReactPaginate
                                                previousLabel={<i className="mdi mdi-chevron-left"></i>}
                                                nextLabel={<i className="mdi mdi-chevron-right"></i>}
                                                breakLabel={'...'}
                                                pageCount={pageCount}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePageClick}
                                                containerClassName={'pagination pagination-rounded justify-content-end mb-0'}
                                                activeClassName={'active'}
                                                pageClassName={'page-item'}
                                                pageLinkClassName={'page-link'}
                                                previousClassName={'page-item'}
                                                previousLinkClassName={'page-link'}
                                                nextClassName={'page-item'}
                                                nextLinkClassName={'page-link'}
                                                breakClassName={'page-item'}
                                                breakLinkClassName={'page-link'}
                                                forcePage={currentPage}
                                            />
                                        )}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Modals */}
                        <ReconciliationMatchModal
                            show={showMatchModal}
                            onHide={() => {
                                setShowMatchModal(false);
                                setSelectedBankTxn(null);
                            }}
                            bankTxn={selectedBankTxn}
                        />



                        <ReconciliationDetailsModal
                            show={showFullView}
                            onHide={() => setShowFullView(false)}
                            transaction={selectedTransaction}
                            journalId={selectedTransaction?.journal_id}
                        />

                        <ReverseReconciliationModal
                            show={showReverseModal}
                            onHide={() => setShowReverseModal(false)}
                            transaction={transactionToReverse}
                        />


                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankReconciliationReport;
